<template>
  <v-container>
    <v-overlay
      :model-value="overlay"
      class="align-center justify-center"
    >
      <div class="text-center">
        <v-progress-circular
          color="primary"
          size="64"
          indeterminate
        ></v-progress-circular>
        <div class="mt-4 text-h6 text-white">
          Processing. Large batch data processing time may exceed 1 minute.
        </div>
      </div>
    </v-overlay>
    <ToolsNavigationDrawer ref="toolsDrawer" />

    <v-stepper alt-labels :items="stepsName" hide-actions :model-value="stepId">
      <!-- step1 -->
      <template v-slot:[`item.1`]>
        <v-card flat>
          <v-card-title class="d-flex align-center">
             <span class="text-subtitle-1  text-sm-body-2">Upload Data &nbsp;</span>
            <v-icon class="mr-2">mdi mdi-help-circle</v-icon>
            <v-btn
              variant="text"
              color="primary"
              @click="showDataFormatDialog = true"
              class="pa-0"
              style="text-transform: none; font-size: 0.875rem; min-width: auto; letter-spacing: normal;"
            >
              See data format
            </v-btn>
          </v-card-title>
          <v-file-input
            v-model="dataFiles"
            clearable
            label="Data files (supports multiple files)"
            variant="solo-filled"
            show-size
            multiple
            accept=".xlsx,.xls,.csv,.txt"
            prepend-icon="mdi-file-upload"
            :rules="fileRules"
            class="text-subtitle-1"
            @change="handleDataFileChange"
            @update:model-value="handleDataFileChange">
          </v-file-input>
          
          <!-- 文件信息展示区域 -->
          <div v-if="fileInfoList && fileInfoList.length > 0" class="file-info-section mt-3">
            <v-card variant="outlined" class="pa-3">
              <div class="text-subtitle-2 font-weight-medium mb-3">
                Selected Files ({{ fileInfoList.length }})
              </div>
              
              <div v-for="(fileInfo, index) in fileInfoList" :key="index" class="file-item" :class="{ 'mb-3': index < fileInfoList.length - 1 }">
                <div class="d-flex align-center">
                  <!-- 文件图标 -->
                  <v-avatar size="40" class="mr-3">
                    <v-icon size="24" :color="getFileIconColor(fileInfo.type)">
                      {{ getFileIcon(fileInfo.name) }}
                    </v-icon>
                  </v-avatar>
                  
                  <!-- 文件信息 -->
                  <div class="flex-grow-1">
                    <div class="text-subtitle-2 font-weight-medium">{{ fileInfo.name }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ formatFileSize(fileInfo.size) }} • {{ getFileTypeText(fileInfo.name) }}
                    </div>
                  </div>
                  
                  <!-- 状态图标 -->
                  <div class="ml-3">
                    <v-icon v-if="fileUploadStatus === 'success'" color="success" size="24">
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-else-if="fileUploadStatus === 'error'" color="error" size="24">
                      mdi-alert-circle
                    </v-icon>
                    <v-progress-circular 
                      v-else-if="fileUploadStatus === 'uploading'" 
                      indeterminate 
                      color="primary" 
                      size="24"
                    ></v-progress-circular>
                    <v-icon v-else color="info" size="24">
                      mdi-file-check
                    </v-icon>
                  </div>
                </div>
              </div>
              
              <!-- 上传进度条 -->
              <div v-if="fileUploadStatus === 'uploading'" class="mt-3">
                <v-progress-linear 
                  indeterminate 
                  color="primary"
                  height="4"
                ></v-progress-linear>
                <div class="text-caption text-center mt-1 text-medium-emphasis">
                  正在处理 {{ fileInfoList.length }} 个文件...
                </div>
              </div>
              
              <!-- 成功状态信息 -->
              <div v-if="fileUploadStatus === 'success' && convertResult" class="mt-2">
                <v-chip size="small" color="success" variant="tonal">
                  <v-icon start>mdi-check</v-icon>
                  已成功转换 {{ convertResult.converted_count }}/{{ convertResult.total_count }} 个ID
                </v-chip>
              </div>
              
              <!-- 错误状态信息 -->
              <div v-if="fileUploadStatus === 'error'" class="mt-2">
                <v-chip size="small" color="error" variant="tonal">
                  <v-icon start>mdi-alert</v-icon>
                  上传失败
                </v-chip>
              </div>
            </v-card>
          </div>
          
          <!-- 分隔符和输入框在同一行 -->
          <v-row align="start" class="mt-4">
            <v-col cols="auto" class="d-flex align-center">
              <span class="separator-text">or</span>
            </v-col>
            <v-col>
              <v-textarea
                v-model="userInputIds"
                label="Enter IDs"
                placeholder="Enter vocabulary, one per line, e.g.:&#10;OMIM:181500&#10;OMIM:273300&#10;7440-48-4"
                variant="solo-filled"
                rows="4"
                clearable
                hide-details
                class="text-subtitle-1"
                @input="handleIdsInput"
              ></v-textarea>
            </v-col>
          </v-row>
 <!-- 最外层：md 及以上用 flex 布局，以下用普通块级 -->
  <div class="d-md-flex align-center justify-space-between mb-2">
    <!-- checkbox：md 及以上不要底边距 -->
    <v-checkbox
      v-model="useExampleData"
      label="Try example data"
      hide-details
      @change="handleExampleDataChange"
            ></v-checkbox>

    <!-- 按钮组：md 及以上自动靠右，以下单独一行并右对齐 -->
    <div
      class="d-flex flex-wrap gap-2 mt-2 mt-md-0 justify-end"
    >
      <v-btn
        color="primary"
        variant="outlined"
        size="small"
        prepend-icon="mdi-download"
        class="mr-2"
        @click="downloadExampleFile('txt')"
      >
        txt
      </v-btn>

      <v-btn
        color="primary"
        variant="outlined"
        size="small"
        prepend-icon="mdi-download"
        class="mr-2"
        @click="downloadExampleFile('csv')"
      >
        csv
      </v-btn>

      <v-btn
        color="primary"
        variant="outlined"
        size="small"
        prepend-icon="mdi-download"
        @click="downloadExampleFile('xlsx')"
      >
        xlsx
      </v-btn>
    </div>
  </div>

          <!-- 数据验证提示 -->
          <div v-if="stepId === 2 && (!vocaRowData || vocaRowData.length === 0)" class="validation-hint mt-3">
            <v-alert type="warning" variant="tonal" density="compact">
              <v-icon start>mdi-alert</v-icon>
              请先在第一步完成数据转换
            </v-alert>
          </div>
          
          <div v-if="stepId === 2 && vocaRowData && vocaRowData.length > 0 && !vocaRowData.some(row => row.selected === true)" class="validation-hint mt-3">
            <v-alert type="warning" variant="tonal" density="compact">
              <v-icon start>mdi-alert</v-icon>
              请至少选择一条数据进行处理
            </v-alert>
          </div>

        </v-card>
        
        <!-- 转换结果信息对话框 -->
        <v-dialog v-model="convertDialog" max-width="500">
          <v-card>
            <v-card-title class="text-h6">
              数据转换结果
            </v-card-title>
            <v-card-text v-if="convertResult">
              <v-list>
                <v-list-item>
                  <v-list-item-title>转换状态</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip :color="convertResult.success ? 'success' : 'error'" size="small">
                      {{ convertResult.success ? '成功' : '失败' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>转换消息</v-list-item-title>
                  <v-list-item-subtitle>{{ convertResult.message }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>总数量</v-list-item-title>
                  <v-list-item-subtitle>{{ convertResult.total_count }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>成功转换数量</v-list-item-title>
                  <v-list-item-subtitle>{{ convertResult.converted_count }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>处理时间</v-list-item-title>
                  <v-list-item-subtitle>{{ convertResult.processing_time }}秒</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="convertDialog = false">确定</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        
        <!-- 数据格式说明对话框 -->
        <v-dialog v-model="showDataFormatDialog" max-width="900" persistent scrollable>
          <v-card>
            <v-card-title class="text-h6 d-flex align-center">
              Data Format Requirements
              <v-spacer></v-spacer>
              <v-btn @click="showDataFormatDialog = false" size="small" variant="text" class="square-close-btn">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text style="max-height: 70vh; overflow-y: auto;">
              <div class="text-body-2 data-format-content" style="line-height: 1.7;">
                <p>In the current E-BIO module, several canonical linkage models are implemented to integrate the "Exposure–Biology–Disease" nexus: one-layer modes of Exposure–GO–Disease (EGoD), Exposure–Gene–Disease (EGeD), Exposure–Protein–Disease (EPD), and Exposure–Pathway–Disease (EPaD)—and a two-layer mode of Exposure–Protein–Protein–Disease (EPPD).</p>

                <p>To run an analysis, provide node IDs either by uploading a file (see the three example files for formatting) or by using the input box and clicking "Try example data." Node dictionaries (DB_Exposure, DB_Disease) can be downloaded from: <a href="http://www.exposomex.cn/#/database101" target="_blank" style="color: #1976d2;">http://www.exposomex.cn/#/database101</a>.</p>

                <p>In the current version, E-BIO accepts IDs only (names are not supported). Please enter IDs according to the following rules:</p>

                <ul>
                  <li>
                    <strong>Exposure:</strong> CAS Registry Number, SMILES, or InChIKey (e.g., CC(C)(C1=CC=C(C=C1)O)C2=CC=C(C=C2)O; 80-05-7; IISBACLAFKSPIT-UHFFFAOYSA-N). Users can search it using PubChem (https://pubchem.ncbi.nlm.nih.gov)
                    <div class="ml-4 mt-2">
                      <p class="mb-2">Additionally, the platform defines a set of internally assigned special exposure identifiers and their corresponding exposures as follows:</p>
                      <ul class="mt-0">
                        <li>EX:E0001S (tobacco smoking)</li>
                        <li>EX:E0002S (alcohol consumption)</li>
                        <li>EX:E0003S (mental stress)</li>
                        <li>EX:E0004S (NOₓ exposure)</li>
                        <li>EX:E0005S (PM₂.₅ exposure)</li>
                        <li>EX:E0006S (sleep deprivation)</li>
                        <li>EX:E0007S (heat exposure)</li>
                        <li>EX:E0008S (high-sodium diet)</li>
                        <li>EX:E0009S (UV radiation)</li>
                        <li>EX:E0010S (physical exercise)</li>
                        <li>EX:E0011S (high-fat diet)</li>
                        <li>EX:E0012S (COVID-19)</li>
                        <li>EX:E0013S (noise pollution)</li>
                        <li>EX:E0014S (traffic-related air pollution)</li>
                      </ul>
                    </div>
                  </li>
                  <li><strong>Disease:</strong> OMIM or MeSH ID (e.g., OMIM:605543; MeSH:D012141). Users can search it using OMIM (https://www.omim.org) and MESH (https://www.ncbi.nlm.nih.gov/mesh). The prefixes of "OMIM:" and "MESH:" should be added before the ID</li>
                </ul>

                <p>Database coverage is as follows: DB_Exposure includes ≈119 million exposures with aliases, IUPAC names, CID, DTXSID, CASRN, InChIKey, and SMILES (when available); DB_Disease includes ≈17 thousand disease IDs; DB_Protein includes ≈154 thousand proteins (primarily human, with a small fraction from mouse/rat); DB_Gene includes ≈51 thousand genes across supported species; DB_Pathway includes ≈572 thousand pathways (mainly human entries curated from CTD); and DB_GO includes 43 thousand GO terms. For convenience, some nodes (e.g., exposures, proteins, diseases) are also assigned internal IDs prefixed with EX, which users may use to build networks.</p>

                <p>For further details, see: Wang et al. "ExposomeX: Development of an Integrative Exposomic Platform to Expedite Discovery of the 'Exposure–Biology–Disease' Nexus." Environmental Science & Technology. 2025 Jul 8;59(26):13251–13263. doi: <a href="https://doi.org/10.1021/acs.est.5c05956" target="_blank" style="color: #1976d2;">10.1021/acs.est.5c05956</a>.</p>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </template>

      <!-- step2 -->
      <template v-slot:[`item.2`]>
        <v-card flat>
          <v-card-title class="d-flex align-center text-subtitle-1 text-sm-body-2">
            <span>Tidy Data</span>
            <v-spacer></v-spacer>
            <v-btn
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-download"
              @click="downloadTidyData"
              :disabled="!selected || selected.length === 0"
            >
              Download Current Data
            </v-btn>
          </v-card-title>
          <v-text-field v-model="searchCondition" label="Search" prepend-inner-icon="mdi-magnify"
          variant="outlined" hide-details single-line></v-text-field>
          <v-row style="height: 37px"></v-row>
        <v-row justify="center">
          <v-col cols="12">
            <div style="width: 100%; margin: 0 auto;">
              <v-data-table
                v-model="selected"
                :headers="vocaHeaders"
                :items="vocaRowData"
                :search="searchCondition"
                :items-per-page="vocaItemsPerPage"
                show-select
                item-value="id"
                :fixed-header="true"
                :height="400"
                v-model:sort-by="vocaSortBy"
                must-sort
                class="tidy-data-table"
                style="border: 1px solid #212121;border-radius: 4px"
                @update:model-value="onSelectionChanged">

                <!-- 自定义表头，显式显示排序图标 -->
                <template v-for="header in vocaHeaders" :key="header.key" v-slot:[`header.${header.key}`]="{ column, getSortIcon }">
                  <div class="d-flex align-center">
                    <span>{{ header.title }}</span>
                    <v-icon
                      v-if="header.sortable !== false"
                      :icon="getSortIcon(column)"
                      size="16"
                      class="ml-1 sort-icon"
                    ></v-icon>
                  </div>
                </template>

                <!-- 自定义单元格内容，添加高亮功能 -->
                <template v-for="header in vocaHeaders" :key="header.key" v-slot:[`item.${header.key}`]="{ item }">
                  <span v-html="highlightText(item[header.key], searchCondition)"></span>
                </template>
              </v-data-table>
            </div>
          </v-col>
        </v-row>
      </v-card>

        

      </template>

      <!-- step3 -->
      <template v-slot:[`item.3`]>
        <div style="margin-left: 10px; margin-right: 10px;">
          <!-- Mode of modeling section -->
          <v-row style="margin-bottom: 20px;">
            <v-col cols="12">
              <v-card flat variant="outlined">
                <v-card-title class="text-subtitle-1 text-sm-body-2 d-flex align-center">
                  <span>Select Mode &nbsp;</span>
                  <v-icon class="mr-2">mdi-help-circle</v-icon>
                  <v-btn
                    variant="text"
                    color="primary"
                    @click="showModeInstructionDialog = true"
                    class="pa-0"
                    style="text-transform: none; font-size: 0.875rem; min-width: auto; letter-spacing: normal;"
                  >
                    See mode instruction
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12">
                      <v-row style="margin-top: 10px;" align="center">
                        <v-col cols="12" md="8" sm="12">
                          <v-list density="compact" class="pa-0" style="line-height: 1.1;">
                            <v-list-item class="px-0 py-0" style="min-height: 32px;">
                              <template v-slot:prepend>
                                <v-checkbox
                                  v-model="mode"
                                  value="EPPD"
                                  hide-details
                                  @change="handleModeChange('EPPD')"
                                ></v-checkbox>
                              </template>
                              <v-list-item-title class="text-body-2 font-weight-medium">EPPD: Exposure-Protein-Protein-Disease</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="px-0 py-0" style="min-height: 32px;">
                              <template v-slot:prepend>
                                <v-checkbox
                                  v-model="mode"
                                  value="EPD"
                                  hide-details
                                  @change="handleModeChange('EPD')"
                                ></v-checkbox>
                              </template>
                              <v-list-item-title class="text-body-2 font-weight-medium">EPD: Exposure-Protein-Disease</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="px-0 py-0" style="min-height: 32px;">
                              <template v-slot:prepend>
                                <v-checkbox
                                  v-model="mode"
                                  value="EGoD"
                                  hide-details
                                  @change="handleModeChange('EGoD')"
                                ></v-checkbox>
                              </template>
                              <v-list-item-title class="text-body-2 font-weight-medium">EGoD: Exposure-GO-Disease</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="px-0 py-0" style="min-height: 32px;">
                              <template v-slot:prepend>
                                <v-checkbox
                                  v-model="mode"
                                  value="EGeD"
                                  hide-details
                                  @change="handleModeChange('EGeD')"
                                ></v-checkbox>
                              </template>
                              <v-list-item-title class="text-body-2 font-weight-medium">EGeD: Exposure-Gene-Disease</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="px-0 py-0" style="min-height: 32px;">
                              <template v-slot:prepend>
                                <v-checkbox
                                  v-model="mode"
                                  value="EPaD"
                                  hide-details
                                  @change="handleModeChange('EPaD')"
                                ></v-checkbox>
                              </template>
                              <v-list-item-title class="text-body-2 font-weight-medium">EPaD: Exposure-Pathway-Disease</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-col>
                        <v-col cols="12" sm="12" md="4"  class="d-flex justify-center">
                          <div style="max-width: 100%; max-height: 220px; display: flex; align-items: center;">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 181.96 160.77" style="width: 110%; height: auto; max-height: 220px;">
                              <g>
                                <g>
                                  <g>
                                    <line stroke="#3e3a39" stroke-width="0" x1="135.89" y1="100.92" x2="38.89" y2="58.82"/>
                                    <g>
                                      <line stroke="#727171" stroke-width=".25" stroke-miterlimit="10" fill="none" x1="134.94" y1="100.5" x2="38.89" y2="58.82"/>
                                      <polygon fill="#727171" stroke-width="0" points="134.74 99.71 134.74 100.42 134.23 100.9 135.89 100.92 134.74 99.71"/>
                                    </g>
                                  </g>
                                  <g>
                                    <line stroke="#727171" stroke-width=".25" stroke-miterlimit="10" fill="none" x1="130.8" y1="110.13" x2="47.5" y2="110.13"/>
                                    <polygon fill="#727171" stroke-width="0" points="130.3 109.49 130.58 110.13 130.3 110.78 131.84 110.13 130.3 109.49"/>
                                  </g>
                                  <rect fill="#7f4f21" stroke="#fff" stroke-width=".5" stroke-miterlimit="10" x=".25" y="44.19" width="46.35" height="16.8" rx="4.32" ry="4.32"/>
                                  <rect fill="#00a29a" stroke="#fff" stroke-width=".5" stroke-miterlimit="10" x=".25" y="101.15" width="46.35" height="16.8" rx="4.32" ry="4.32"/>
                                  <rect fill="#c30d23" stroke="#c30d23" stroke-width=".5" stroke-miterlimit="10" x="66.37" y="143.09" width="46.28" height="16.8" rx="4.32" ry="4.32"/>
                                  <text fill="#fff" font-family="Georgia-Bold, Georgia" font-size="10.2" font-weight="700" transform="translate(74.66 153.97) scale(.77 1)"><tspan x="0" y="0">Disease</tspan></text>
                                  <rect fill="#1d2088" stroke="#fff" stroke-width=".5" stroke-miterlimit="10" x="66.15" y=".25" width="46.28" height="16.8" rx="4.32" ry="4.32"/>
                                  <rect fill="#00913a" stroke="#fff" stroke-width=".5" stroke-miterlimit="10" x="133.57" y="44.19" width="48.14" height="16.8" rx="4.32" ry="4.32"/>
                                  <rect fill="#036eb8" stroke="#fff" stroke-width=".5" stroke-miterlimit="10" x="133.57" y="101.12" width="48.14" height="16.8" rx="4.32" ry="4.32"/>
                                  <text fill="#fff" font-family="Georgia-Bold, Georgia" font-size="10.2" font-weight="700" transform="translate(8 56.01) scale(.77 1)"><tspan x="0" y="0">Protein</tspan></text>
                                  <text fill="#fff" font-family="Georgia-Bold, Georgia" font-size="10.2" font-weight="700" transform="translate(10.78 112.55) scale(.77 1)"><tspan x="0" y="0">GO</tspan></text>
                                  <text fill="#fff" font-family="Georgia-Bold, Georgia" font-size="10.2" font-weight="700" transform="translate(147.31 55.88) scale(.77 1)"><tspan x="0" y="0">Gene</tspan></text>
                                  <text fill="#fff" font-family="Georgia-Bold, Georgia" font-size="10.2" font-weight="700" transform="translate(140.84 113.17) scale(.77 1)"><tspan x="0" y="0">Pathway</tspan></text>
                                  <g>
                                    <line stroke="#3e3a39" stroke-width="0" x1="66.37" y1="14.5" x2="15.7" y2="40.42"/>
                                    <g>
                                      <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="66.37" y1="14.5" x2="17.56" y2="39.47"/>
                                      <polygon fill="#036eb8" stroke-width="0" points="17.85 37.87 17.95 39.28 19.03 40.18 15.7 40.42 17.85 37.87"/>
                                    </g>
                                  </g>
                                  <g>
                                    <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="162.35" y1="120.97" x2="116.39" y2="148"/>
                                    <polygon fill="#036eb8" stroke-width="0" points="116.58 146.38 116.76 147.78 117.89 148.61 114.59 149.05 116.58 146.38"/>
                                  </g>
                                  <g>
                                    <line stroke="#727171" stroke-width=".25" stroke-miterlimit="10" fill="none" x1="14.76" y1="65.63" x2="14.76" y2="94.67"/>
                                    <polygon fill="#727171" stroke-width="0" points="14.11 94.17 14.76 94.45 15.4 94.17 14.76 95.71 14.11 94.17"/>
                                  </g>
                                  <g>
                                    <line stroke="#727171" stroke-width=".25" stroke-miterlimit="10" fill="none" x1="164.87" y1="94.83" x2="164.87" y2="63.87"/>
                                    <polygon fill="#727171" stroke-width="0" points="165.51 94.33 164.87 94.61 164.22 94.33 164.87 95.87 165.51 94.33"/>
                                  </g>
                                  <g>
                                    <line stroke="#727171" stroke-width=".25" stroke-miterlimit="10" fill="none" x1="132.55" y1="53.39" x2="48.55" y2="53.39"/>
                                    <polygon fill="#727171" stroke-width="0" points="49.04 52.74 48.76 53.39 49.04 54.04 47.5 53.39 49.04 52.74"/>
                                  </g>
                                  <g>
                                    <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="113.07" y1="12.69" x2="165.62" y2="39.07"/>
                                    <polygon fill="#036eb8" stroke-width="0" points="164.16 39.79 165.23 38.87 165.32 37.47 167.48 40.01 164.16 39.79"/>
                                  </g>
                                  <g>
                                    <line stroke="#3e3a39" stroke-width="0" x1="64.45" y1="149.1" x2="21.47" y2="122.9"/>
                                    <g>
                                      <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="62.67" y1="148.01" x2="21.47" y2="122.9"/>
                                      <polygon fill="#036eb8" stroke-width="0" points="62.5 146.39 62.3 147.78 61.15 148.6 64.45 149.1 62.5 146.39"/>
                                    </g>
                                  </g>
                                  <g>
                                    <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="105.59" y1="140.26" x2="155.62" y2="61.5"/>
                                    <polygon fill="#036eb8" stroke-width="0" points="107.21 140.12 105.82 139.89 105.02 138.73 104.47 142.02 107.21 140.12"/>
                                  </g>
                                  <g>
                                    <line stroke="#727171" stroke-width=".25" stroke-miterlimit="10" fill="none" x1="90.96" y1="20.34" x2="90.96" y2="139.98"/>
                                    <polygon fill="#727171" stroke-width="0" points="90.31 139.49 90.96 139.77 91.61 139.49 90.96 141.03 90.31 139.49"/>
                                  </g>
                                  <g>
                                    <line stroke="#3e3a39" stroke-width="0" x1="82.52" y1="19.48" x2="21.58" y2="95.94"/>
                                    <g>
                                      <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="82.52" y1="19.48" x2="22.88" y2="94.31"/>
                                      <polygon fill="#036eb8" stroke-width="0" points="22.48 92.73 23.15 93.97 24.51 94.35 21.58 95.94 22.48 92.73"/>
                                    </g>
                                  </g>
                                  <g>
                                    <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="99.01" y1="19.48" x2="154.91" y2="97.6"/>
                                    <polygon fill="#036eb8" stroke-width="0" points="153.29 97.56 154.66 97.25 155.4 96.05 156.13 99.3 153.29 97.56"/>
                                  </g>
                                  <g>
                                    <line stroke="#036eb8" stroke-width=".5" stroke-miterlimit="10" fill="none" x1="20.72" y1="64.11" x2="76.51" y2="139.92"/>
                                    <polygon fill="#036eb8" stroke-width="0" points="74.89 139.89 76.26 139.57 76.97 138.36 77.75 141.6 74.89 139.89"/>
                                  </g>
                                  <text fill="#fff" font-family="Georgia-Bold, Georgia" font-size="10.2" font-weight="700" transform="translate(71.15 10.98) scale(.77 1)"><tspan x="0" y="0">Exposure</tspan></text>
                                </g>
                              </g>
                            </svg>
                          </div>
                        </v-col>
                      </v-row>
                      
                      <!-- Run 按钮移动到选项下方 -->
                      <v-row style="margin-top: 20px;">
                        <v-col cols="4" class="d-flex justify-start" style="padding-left: 23px;">
                          <v-btn
                            color="error"
                            variant="tonal"
                            size="large"
                            style="height: 28px;"
                            :loading="biolinkLoading"
                            :disabled="!vocaRowData || vocaRowData.length === 0"
                            @click="transformData"
                          >
                            Run
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>  
            


        </div>
      </template>

      <!-- step4 -->
      <template v-slot:[`item.4`]>
        <div style="margin-left: 10px; margin-right: 10px;">
          <!-- Summary section -->
          <v-row style="margin-bottom: 20px;">
            <v-col cols="12">
              <v-card flat variant="outlined">
                <v-card-title class="text-subtitle-1  text-sm-body-2">Generated Files</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12">

                      
                      <!-- Loading State -->
                      <div v-if="summaryLoading" class="text-center pa-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <p class="mt-2">Loading file structure...</p>
                      </div>

                      <!-- File Structure -->
                      <div v-else-if="summaryData" class="file-structure">

                        

                        
                        <!-- Upload Data Section -->
                        <v-card variant="outlined" class="mb-4">
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="primary">mdi-upload</v-icon>
                            <span class="text-body-2 font-weight-medium">Upload Data</span>
                            <v-chip size="small" color="info" variant="tonal" class="ml-auto">
                              {{ summaryData.directory_structure?.uploadData?.file_count || 0 }} files
                            </v-chip>
                          </v-card-title>
                          <v-card-text>
                            <v-list v-if="summaryData.directory_structure?.uploadData?.files && summaryData.directory_structure.uploadData.files.length > 0">
                              <v-list-item v-for="file in summaryData.directory_structure.uploadData.files" :key="file.filename">
                                <template v-slot:prepend>
                                  <v-icon color="primary">mdi-file-document</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2">{{ file.filename }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                            <div v-else class="text-center pa-4 text-medium-emphasis">
                              No files in this category
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Tidy Data Section -->
                        <v-card variant="outlined" class="mb-4">
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="success">mdi-table</v-icon>
                            <span class="text-body-2 font-weight-medium">Tidy Data</span>
                            <v-chip size="small" color="success" variant="tonal" class="ml-auto">
                              {{ summaryData.directory_structure?.tidy?.file_count || 0 }} files
                            </v-chip>
                          </v-card-title>
                          <v-card-text>
                            <v-list v-if="summaryData.directory_structure?.tidy?.files && summaryData.directory_structure.tidy.files.length > 0">
                              <v-list-item v-for="file in summaryData.directory_structure.tidy.files" :key="file.filename">
                                <template v-slot:prepend>
                                  <v-icon color="success">mdi-file-csv</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2">{{ file.filename }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                            <div v-else class="text-center pa-4 text-medium-emphasis">
                              No files in this category
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Build Model Section -->
                        <v-card variant="outlined" class="mb-4">
                          <v-card-title class="d-flex align-center">
                            <v-icon class="mr-2" color="warning">mdi-graph</v-icon>
                            <span class="text-body-2 font-weight-medium">Build Model</span>
                            <v-chip size="small" color="warning" variant="tonal" class="ml-auto">
                              {{ summaryData.directory_structure?.buildModel?.file_count || 0 }} files
                            </v-chip>
                          </v-card-title>
                          <v-card-text>
                            <v-list v-if="summaryData.directory_structure?.buildModel?.files && summaryData.directory_structure.buildModel.files.length > 0">
                              <v-list-item v-for="file in summaryData.directory_structure.buildModel.files" :key="file.filename">
                                <template v-slot:prepend>
                                  <v-icon color="warning">mdi-file-network</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2">{{ file.filename }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                            <div v-else class="text-center pa-4 text-medium-emphasis">
                              No files in this category
                            </div>
                          </v-card-text>
                        </v-card>

                        <!-- Summary Stats -->
                        <v-card variant="outlined" class="mt-4 pa-4">
                          <div class="d-flex justify-space-between align-center">
                            <div>
                              <div class="text-subtitle-1 font-weight-medium">{{ summaryData.total_files }} Total Files</div>
                            </div>
                          <!-- 大屏：文字按钮 | 小屏：图标按钮 -->
                          <v-btn
                            color="primary"
                            variant="tonal"
                            :icon="$vuetify.display.smAndDown"
                            @click="downloadAllFiles"
                            :loading="downloadLoading"
                            v-bind="$vuetify.display.smAndDown ? {} : { prependIcon: 'mdi-download' }"
                          >
                            <!-- 小屏隐藏文字 -->
                            <span class="d-none d-md-inline">Download All Files</span>
                            <!-- 小屏只显示图标 -->
                            <v-icon class="d-md-none">mdi-download</v-icon>
                          </v-btn>
                          </div>
                        </v-card>
                      </div>

                      <!-- Error State -->
                      <v-alert v-else-if="summaryError" type="error" variant="tonal" class="mt-4">
                        <v-icon start>mdi-alert-circle</v-icon>
                        Failed to load file structure: {{ summaryError }}
                      </v-alert>

                      <!-- No Data State -->
                      <v-alert v-else-if="!summaryLoading && !summaryData" type="info" variant="tonal" class="mt-4">
                        <v-icon start>mdi-information</v-icon>
                        No file structure data available. Please check if the session has completed successfully.
                      </v-alert>


                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </template>

      <!-- 步骤导航按钮 -->
      <div class="stepper-actionsy">
        <v-divider class="mx-4 mb-4"></v-divider>
        <div class="step-content">
          <v-btn :disabled="stepId == 1" class="mr-4" size="large" variant="tonal" color="primary"
            @click="stepperPre">Prev</v-btn>
          <v-btn :disabled="!canProceedToNext || loading || biolinkLoading" class="mr-4" size="large" variant="tonal" color="primary"
            @click="stepperNext">
            <!-- 显示loading状态或按钮文本 -->
            <span v-if="loading || biolinkLoading">Loading...</span>
            <span v-else>{{ isLastStep ? 'Exit' : 'Next' }}</span>
          </v-btn>
        </div>
      </div>
    </v-stepper>

    <!-- 下载文件名弹窗 -->
    <v-dialog v-model="downloadDialogVisible" max-width="400">
      <v-card>
        <v-card-title>Download Files</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="downloadFileName"
            label="File Name"
            variant="outlined"
            density="compact"
            hide-details
          >
            <template v-slot:append-inner>
              <span class="text-grey">.zip</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="downloadDialogVisible = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmDownload" :loading="downloadLoading">
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mode Instruction 弹窗 -->
    <v-dialog v-model="showModeInstructionDialog" max-width="900" persistent scrollable>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          Biological Link Analysis (E-BIO)
          <v-spacer></v-spacer>
          <v-btn @click="showModeInstructionDialog = false" size="small" variant="text" class="square-close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text style="max-height: 70vh; overflow-y: auto;">
          <div class="text-body-2 mode-instruction-content" style="line-height: 1.7;">
            <p>The E-BIO module is designed to link environmental exposures to diseases through intermediate biological layers, enabling mechanistic interpretation of exposomic associations. To balance biological interpretability, data availability, and mechanistic rigor, E-BIO provides both two-layer and single-layer interaction models. These models represent different assumptions about how exposures propagate through biological systems toward disease outcomes.</p>

            <h3 class="text-h6 mt-4 mb-2">Recommended Strategy in Practice</h3>
            <p>In practice, E-BIO is designed to support a progressive analytical strategy:</p>
            <ol>
              <li>Start with EGoD for broad screening and functional insight</li>
              <li>Advance to EPPD to refine mechanisms and explore interaction networks</li>
              <li>Use other single-layer models (EPD, EGeD, EPaD) as complementary views depending on data availability</li>
            </ol>
            <p>By combining EGoD (breadth) and EPPD (depth), E-BIO enables users to move efficiently from association discovery to mechanistic interpretation, supporting robust and biologically grounded exposomic analyses.</p>

            <h3 class="text-h6 mt-4 mb-2">Two-Layer Model (Recommended for Mechanistic Analysis)</h3>

            <h4 class="text-subtitle-1 font-weight-bold mt-3">(1) Exposure–Protein–Protein–Disease (EPPD) ⭐⭐</h4>
            <p>The EPPD model is the most mechanistically constrained interaction mode in E-BIO. It links environmental exposures to diseases through protein–protein interaction (PPI) networks, allowing exposures to affect diseases via interacting molecular modules rather than isolated targets.</p>
            <p><strong>Why EPPD is recommended:</strong></p>
            <ul>
              <li>Reflects the networked nature of biological systems</li>
              <li>Captures indirect and cascading effects of exposures</li>
              <li>Enables identification of shared pathways and exposure–exposure interactions</li>
              <li>Particularly suitable for mixture toxicity and mode-of-action–oriented risk assessment</li>
            </ul>
            <p><strong>Typical use cases:</strong></p>
            <ul>
              <li>Studies aiming to infer mechanistic pathways</li>
              <li>Analyses of multiple exposures acting on shared protein networks</li>
              <li>Prioritization of key mediator proteins or subnetworks</li>
            </ul>
            <p>Because many diseases arise from coordinated perturbations of interacting proteins, EPPD provides the highest biological realism within the E-BIO framework and is therefore strongly recommended when sufficient protein interaction data are available.</p>

            <h3 class="text-h6 mt-4 mb-2">Single-Layer Models (Recommended for Screening and Hypothesis Generation)</h3>
            <p>Single-layer models connect exposures to diseases through one intermediate biological layer. These models are computationally efficient, broadly applicable, and well suited for exploratory analyses.</p>

            <h4 class="text-subtitle-1 font-weight-bold mt-3">(2) Exposure → Gene Ontology (GO) → Disease (EGoD) ⭐⭐</h4>
            <p>EGoD links exposures to diseases via perturbed biological processes, molecular functions, or cellular components represented by GO terms. Rather than focusing on specific molecules, it emphasizes functional and phenotypic disruption.</p>
            <p><strong>Why EGoD is recommended:</strong></p>
            <ul>
              <li>Broadest coverage among all models</li>
              <li>Robust to sparse or incomplete molecular data</li>
              <li>Ideal for early-stage screening and hypothesis generation</li>
              <li>Easily integrates with enrichment analysis of omics data</li>
            </ul>
            <p><strong>Typical use cases:</strong></p>
            <ul>
              <li>Large-scale exposome screening</li>
              <li>Studies focusing on functional outcomes (e.g., inflammation, oxidative stress, neurodevelopment)</li>
              <li>Situations where molecular targets are unknown or uncertain</li>
            </ul>
            <p>Together with EPPD, EGoD is recommended as a default starting point in E-BIO analyses.</p>

            <h4 class="text-subtitle-1 font-weight-bold mt-3">(3) Exposure → Protein → Disease (EPD) ⭐</h4>
            <p>EPD directly links exposures to diseases through protein targets, treating proteins as primary functional mediators.</p>
            <p><strong>Use cases:</strong></p>
            <ul>
              <li>Toxicology-oriented analyses</li>
              <li>Studies with known chemical–protein interactions</li>
              <li>Target-centric interpretation of exposure effects</li>
            </ul>
            <p><strong>Strengths:</strong></p>
            <ul>
              <li>Clear molecular anchoring</li>
              <li>Higher specificity than GO- or gene-based models</li>
            </ul>

            <h4 class="text-subtitle-1 font-weight-bold mt-3">(4) Exposure → Gene → Disease (EGeD) ⭐</h4>
            <p>EGeD connects exposures to diseases through gene-level associations, capturing transcriptional or genetic susceptibility signals.</p>
            <p><strong>Use cases:</strong></p>
            <ul>
              <li>Integration with transcriptomic or genetic data</li>
              <li>Identification of candidate susceptibility genes</li>
            </ul>
            <p><strong>Strengths:</strong></p>
            <ul>
              <li>Direct compatibility with gene expression analyses</li>
              <li>Intermediate specificity between GO- and protein-based models</li>
            </ul>

            <h4 class="text-subtitle-1 font-weight-bold mt-3">(5) Exposure → Pathway → Disease (EPaD) ⭐</h4>
            <p>EPaD links exposures to diseases via annotated biological pathways, integrating multiple genes or proteins into coordinated functional units.</p>
            <p><strong>Use cases:</strong></p>
            <ul>
              <li>Systems-level interpretation</li>
              <li>Pathway enrichment–based analyses</li>
            </ul>
            <p><strong>Strengths:</strong></p>
            <ul>
              <li>Captures coordinated biological responses</li>
              <li>Reduces noise from single-molecule associations</li>
            </ul>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import chroma from 'chroma-js';
import { mapGetters, mapActions } from 'vuex';
import ToolsNavigationDrawer from '@/components/ToolsNavigationDrawer.vue';

export default {
  name: "E-bio",
  components: {
    ToolsNavigationDrawer
  },
  data() {
    return {
      loading: false,
      overlay:false,

      gridApi: null,
      columnApi: null,

      tipMsg: "Learn more",
      transBtnPosition: "end",

      dataFileExisted: false,
      vocabFileExisted: false,
      dataFiles: null, // 支持多个文件
      vocabFile: null,
      userInputIds: '', // 用户输入的词汇列表
      
      // 文件信息和上传状态
      fileInfoList: [], // 存储多个文件信息 [{name, size, type}, ...]
      fileUploadStatus: 'idle', // idle, uploading, success, error
      
      // 文件验证规则
      fileRules: [
        value => {
          if (!value || value.length === 0) return true;
          const file = Array.isArray(value) ? value[0] : value;
          if (!file || !file.name) return 'Invalid file format';
          const allowedTypes = ['.xlsx', '.xls', '.csv', '.txt'];
          const fileName = file.name.toLowerCase();
          const isValid = allowedTypes.some(type => fileName.endsWith(type));
          return isValid || 'Only Excel (.xlsx, .xls), CSV (.csv) and TXT (.txt) files are supported';
        },
        value => {
          if (!value || value.length === 0) return true;
          const file = Array.isArray(value) ? value[0] : value;
          if (!file || file.size === undefined) return 'Invalid file format';
          const maxSize = 50 * 1024 * 1024; // 50MB
          return file.size <= maxSize || 'File size cannot exceed 50MB';
        }
      ],

      searchCondition: '',
      selected: [],
      vocaHeaders: [],
      vocaRowData: [],
      vocaSortBy: [],  // 用于跟踪排序状态
      lastSortColumn: null,  // 记录上次排序的列






      useExampleData: false,  // 控制是否使用示例数据
      mode: 'EPPD',
      
      // 示例数据IDs（所有类型使用相同的示例数据）
      exampleDataIds: [
        "OMIM:181500",
        "OMIM:273300", 
        "OMIM:604302",
        "OMIM:244200",
        "7440-48-4",
        "333-41-5",
        "1746-01-6"
      ],

      // API转换相关数据
      convertResult: null,
      convertLoading: false,
      convertDialog: false,
      biolinkResult: null,
      biolinkLoading: false,

      // Track previous model selection for next button state management
      lastSuccessfulModel: null, // 记录上次成功运行的模型

      // Pre-opened window for chart visualization
      preOpenedChartWindow: null, // 预先打开的图表窗口引用

      // Network graph related data
      activeTab: 'nodes',
      activeTabFull: 'nodes',
      searchQuery: '',
      selectedNodes: [],
      selectedEdges: [],
      
      // Layout settings
      nodeType: '1',
      nodeNameType: '2',
      nodevSize: 10,
      edgeSize: 10,
      frontSize: 10,
      nodeTransparency: 50,
      lineTransparency: 50,
      linecolor: '#515F6E',
      colorList: ['#5470c6', '#fac858', '#fac858', '#fac858', '#fac858', '#fac858', '#3ba272'],
      categories: [],
      nodeLoad: false,
      nodeScaleFactor: 1,
      option: {},
      
      nodeHeaders: [
        { title: 'EXID', key: 'EXID', sortable: true },
        { title: 'Preferred name', key: 'Preferred name', sortable: true },
        { title: 'Group name', key: 'Group name', sortable: true },
        { title: 'Degree', key: 'Degree', sortable: true },
        { title: 'ID', key: 'ID', sortable: true },
        { title: 'Synonyms', key: 'Synonyms', sortable: true }
      ],
      edgeHeaders: [
        { title: 'Source', key: 'source', sortable: true },
        { title: 'Target', key: 'target', sortable: true },
        { title: 'Interaction', key: 'interaction', sortable: true },
        { title: 'Source group', key: 'source_group', sortable: true },
        { title: 'Target group', key: 'target_group', sortable: true },
        { title: 'Database', key: 'database', sortable: true },
        { title: 'Edge type', key: 'edge_type', sortable: true }
      ],
      nodeHeadersFull: [
        { title: 'EXID', key: 'EXID', sortable: true },
        { title: 'Preferred name', key: 'Preferred name', sortable: true },
        { title: 'Group name', key: 'Group name', sortable: true },
        { title: 'Degree', key: 'Degree', sortable: true },
        { title: 'ID', key: 'ID', sortable: true },
        { title: 'Synonyms', key: 'Synonyms', sortable: true }
      ],
      edgeHeadersFull: [
        { title: 'Source', key: 'source', sortable: true },
        { title: 'Target', key: 'target', sortable: true },
        { title: 'Interaction', key: 'interaction', sortable: true },
        { title: 'Source group', key: 'source_group', sortable: true },
        { title: 'Target group', key: 'target_group', sortable: true },
        { title: 'Database', key: 'database', sortable: true },
        { title: 'Edge type', key: 'edge_type', sortable: true }
      ],
      processedNodes: [],
      processedEdges: [],
      networkChart: null,

      stepsName: ['Upload Data', 'Tidy', 'Build Model', 'Summary'],
      stepId: 1,
      themeClass: "ag-theme-quartz",
      vocaColumnDefs: [],
      dataHeaders: [],
      dataColumnDefs: [],
      dataRowData: [],
      rowSelection: {
        mode: 'multiRow',
        selectAll: 'filtered',
      },

      vocaItemsPerPage: 10,
      dataItemsPerPage: 10,
      
      // Summary page related data
      summaryData: null,
      summaryLoading: false,
      summaryError: null,
      downloadLoading: false,

      // 下载弹窗
      downloadDialogVisible: false,
      downloadFileName: '',

      // Data format dialog
      showDataFormatDialog: false,
      // Mode instruction dialog
      showModeInstructionDialog: false,
    }
  },
  async mounted() {
    console.log('组件挂载，初始化数据...');

    // 检查 URL 参数中是否有 session
    const urlParams = new URLSearchParams(window.location.search);
    const urlSession = urlParams.get('session');

    if (urlSession) {
      console.log('从 URL 参数中获取到 session:', urlSession);
      // 如果 URL 中有 session 参数，设置到 Vuex store 中
      this.$store.commit('SET_SESSION_UUID', urlSession);
      console.log('已将 URL session 设置到 store');
    } else if (!this.sessionUuid) {
      console.log('URL 中没有 session 参数，且 store 中也没有 session，创建新会话...');
      // 如果没有 session，创建一个新的
      try {
        const sessionData = await this.createNewSession();
        console.log('新会话创建成功:', sessionData);
      } catch (error) {
        console.error('创建新会话失败:', error);
        // 如果创建失败，跳转回首页
        this.$router.push('/start-all');
        return;
      }
    }

    console.log('Session UUID:', this.sessionUuid);
    console.log('Session Name:', this.sessionName);
    
    // 初始化会话步骤为1
    await this.initializeSession();
    
    // 添加窗口大小变化监听器
    window.addEventListener('resize', this.handleResize);
    
    // 如果当前已经是第四步，自动获取文件结构
    if (this.stepId === 4) {
      console.log('组件挂载时已经是第四步，自动获取文件结构');
      this.$nextTick(() => {
        this.fetchSummaryData();
      });
    }
  },
  
  beforeUnmount() {
    // 移除事件监听器
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    ...mapGetters(['sessionUuid', 'sessionName', 'sessionStatus']),
    isLastStep() {
      return this.stepId === this.stepsName.length;
    },
    
    // 检查是否有有效的数据输入
    hasValidInput() {
      // 检查是否有上传文件
      const hasFiles = this.dataFileExisted && this.dataFiles && this.dataFiles.length > 0;
      
      // 检查是否有用户输入的词汇
      const hasUserInput = this.userInputIds && this.userInputIds.trim().length > 0;
      
      // 检查是否选中了示例数据
      const hasExampleData = this.useExampleData;
      
      return hasFiles || hasUserInput || hasExampleData;
    },
    
    // 检查是否可以进入下一步
    canProceedToNext() {
      if (this.stepId === 1) {
        return this.hasValidInput && !this.loading;
      } else if (this.stepId === 2) {
        // 第二步：必须有数据且必须选择至少一条数据
        const hasSelectedData = this.vocaRowData && this.vocaRowData.length > 0 && 
                               this.vocaRowData.some(row => row.selected === true);
        return hasSelectedData && !this.loading;
      } else if (this.stepId === 3) {
        // 第三步：如果有biolink结果或者会话状态已经是1（已完成），都可以进入下一步
        // 新增逻辑：如果模型发生变化且未重新运行，禁用next按钮
        // 简单判断：如果有上次成功的模型记录，且当前模型与之不同，且没有新的成功结果，则禁用
        if (this.lastSuccessfulModel &&
            this.mode !== this.lastSuccessfulModel &&
            !(this.biolinkResult && this.biolinkResult.success)) {
          console.log('模型已变化，禁用next按钮:', this.mode, '!=', this.lastSuccessfulModel);
          return false;
        }
        return (this.biolinkResult && this.biolinkResult.success) || this.isSessionCompleted;
      } else if (this.stepId === 4) {
        // 第四步（Summary页面）：不能再进入下一步
        return true;
      }
      return true;
    },
    
    // 检查会话是否已完成（状态为1）
    isSessionCompleted() {
      // 从store中获取会话状态，如果状态是1表示已完成
      return this.sessionStatus === 1;
    },
    
    filteredNodes() {
      if (!this.searchQuery) {
        return this.processedNodes;
      }
      const query = this.searchQuery.toLowerCase();
      return this.processedNodes.filter(node => 
        node.EXID.toLowerCase().includes(query) ||
        node['Preferred name'].toLowerCase().includes(query) ||
        node['Group name'].toLowerCase().includes(query)
      );
    },
    filteredEdges() {
      if (!this.searchQuery) {
        return this.processedEdges;
      }
      const query = this.searchQuery.toLowerCase();
      return this.processedEdges.filter(edge => 
        edge.source.toLowerCase().includes(query) ||
        edge.target.toLowerCase().includes(query) ||
        edge.interaction.toLowerCase().includes(query)
      );
    }
  },
  watch: {
    selected: {
      handler(newValue, oldValue) {
        // 只在值真正改变时触发
        if (newValue && newValue.length > 0 && JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          this.onSelectionChanged(newValue);
        }
      },
      deep: true,
      immediate: false
    },

    // 监听步骤变化，自动获取数据
    stepId: {
      handler(newStepId, oldStepId) {
        console.log('步骤变化:', oldStepId, '->', newStepId);
        
        // 当进入第四步时，自动获取文件结构
        if (newStepId === 4) {
          console.log('检测到进入第四步，自动获取文件结构');
          this.$nextTick(() => {
            this.fetchSummaryData();
          });
        }
      },
      immediate: false
    },

    // 监听模型变化，清除之前的构建结果
    mode(newMode, oldMode) {
      // 如果模型改变且之前有成功的构建结果，清除结果以触发重新构建要求
      if (oldMode && newMode !== oldMode && this.biolinkResult && this.biolinkResult.success) {
        console.log('模型从', oldMode, '变为', newMode, '，清除之前的构建结果');
        this.biolinkResult = null;
      }
    },

    // 监听排序变化，实现自定义排序逻辑
    vocaSortBy: {
      handler(newSort) {
        if (!newSort || newSort.length === 0) return;

        const currentSort = newSort[0];

        // 如果是新列，强制设为降序
        if (this.lastSortColumn !== currentSort.key) {
          this.lastSortColumn = currentSort.key;
          if (currentSort.order === 'asc') {
            // 使用 nextTick 确保在 Vuetify 更新后修改
            this.$nextTick(() => {
              this.vocaSortBy = [{ key: currentSort.key, order: 'desc' }];
            });
          }
        }
      },
      deep: true
    }



  },

  methods: {
    ...mapActions(['updateSession', 'createSession']),

    // 处理数据文件上传
    async handleDataFileChange(event) {
      console.log('===== 多文件上传调试信息 =====');
      console.log('事件对象:', event);
      console.log('事件类型:', typeof event);
      
      // 从事件对象或直接的文件数组中获取文件列表
      let files = null;
      
      if (event && event.target && event.target.files) {
        // 从事件对象中获取文件列表
        files = Array.from(event.target.files);
        console.log('从事件对象获取文件列表:', files);
      } else if (Array.isArray(event)) {
        // 直接传入的文件数组
        files = event;
        console.log('直接传入的文件数组:', files);
      } else if (event instanceof FileList) {
        // FileList对象
        files = Array.from(event);
        console.log('从FileList获取文件数组:', files);
      } else if (event instanceof File) {
        // 单个文件对象
        files = [event];
        console.log('单个文件转换为数组:', files);
      } else {
        // 使用v-model绑定的dataFiles
        files = this.dataFiles ? Array.from(this.dataFiles) : [];
        console.log('使用v-model绑定的dataFiles:', files);
      }
      
      if (!files || files.length === 0) {
        console.log('没有找到文件，清空状态');
        this.dataFiles = null;
        this.dataFileExisted = false;
        this.fileInfoList = [];
        this.fileUploadStatus = 'idle';
        return;
      }
      
      console.log('总共选择了', files.length, '个文件');
      
      // 验证所有文件
      const validFiles = [];
      const allowedExtensions = ['.xlsx', '.xls', '.csv', '.txt'];
      const maxSize = 50 * 1024 * 1024; // 50MB
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // 检查文件对象是否有必要的属性
        if (!file || !file.name || file.size === undefined) {
          console.error('File object missing required properties:', file);
          alert(`File ${i + 1} has invalid format, please select valid files`);
          continue;
        }
        
        // 验证文件类型
        const fileName = file.name.toLowerCase();
        const isValidType = allowedExtensions.some(ext => fileName.endsWith(ext));
        
        if (!isValidType) {
          console.error('Unsupported file type:', fileName);
          alert(`File "${file.name}" is not supported. Only Excel (.xlsx, .xls), CSV (.csv) and TXT (.txt) files are supported`);
          continue;
        }
        
        // 验证文件大小
        if (file.size > maxSize) {
          console.error('File too large:', file.size);
          alert(`File "${file.name}" is too large. File size cannot exceed 50MB`);
          continue;
        }
        
        validFiles.push(file);
        console.log(`文件 ${i + 1} 验证通过:`, file.name);
      }
      
      if (validFiles.length === 0) {
        console.log('没有有效文件，清空状态');
        this.dataFiles = null;
        this.dataFileExisted = false;
        this.fileInfoList = [];
        this.fileUploadStatus = 'idle';
        return;
      }
      
      // 保存有效文件信息
      this.dataFiles = validFiles;
      this.dataFileExisted = true;
      this.fileInfoList = validFiles.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type
      }));
      this.fileUploadStatus = 'ready';
      
      // 清空其他输入方式
      if (this.userInputIds) {
        this.userInputIds = '';
        console.log('已清空用户输入，将使用上传文件');
      }
      if (this.useExampleData) {
        this.useExampleData = false;
        console.log('已取消示例数据，将使用上传文件');
      }
      
      console.log(`成功处理 ${validFiles.length} 个文件:`, validFiles.map(f => f.name));
      console.log('================================');
    },
    
    // 读取文件内容
    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });
    },
    
    // 处理用户输入的词汇
    handleIdsInput() {
      console.log('用户输入词汇:', this.userInputIds);

      // 如果正在填充示例数据，跳过检查避免干扰
      if (this._fillingExampleData) {
        console.log('正在填充示例数据，跳过 handleIdsInput 检查');
        return;
      }

      // 如果用户开始输入词汇，清空文件上传
      if (this.userInputIds && this.userInputIds.trim()) {
        if (this.dataFiles) {
          this.dataFiles = null;
          this.dataFileExisted = false;
          this.fileInfoList = [];
          this.fileUploadStatus = 'idle';
          console.log('已清空文件上传，将使用用户输入词汇');
        }

        // 检查用户输入是否与示例数据一致，如果不一致则取消示例数据选择
        if (this.useExampleData) {
          const currentIds = this.userInputIds.split('\n').map(id => id.trim()).filter(id => id.length > 0);
          const exampleIds = this.exampleDataIds;
          const isExampleData = currentIds.length === exampleIds.length &&
                               currentIds.every(id => exampleIds.includes(id));

          if (!isExampleData) {
            this.useExampleData = false;
            console.log('用户修改了输入内容，已取消示例数据选择');
          }
        }
      } else {
        // 如果输入框为空（用户手动清空），取消示例数据选择
        if (this.useExampleData) {
          this.useExampleData = false;
          console.log('输入框已清空，取消示例数据选择');
        }
      }
    },
    
    // 处理示例数据选择变化
    handleExampleDataChange() {
      console.log('示例数据选择状态:', this.useExampleData);

      // 如果选择使用示例数据，清空其他输入并填充示例数据
      if (this.useExampleData) {
        if (this.dataFiles) {
          this.dataFiles = null;
          this.dataFileExisted = false;
          this.fileInfoList = [];
          this.fileUploadStatus = 'idle';
          console.log('已清空文件上传，将使用示例数据');
        }
        // 设置标志，表示正在填充示例数据，避免 handleIdsInput 干扰
        this._fillingExampleData = true;
        // 直接填充示例数据到输入框（换行格式）
        this.userInputIds = this.exampleDataIds.join('\n');
        console.log('已填充示例数据到输入框:', this.userInputIds);
        // 使用 nextTick 确保 DOM 更新后再重置标志
        this.$nextTick(() => {
          this._fillingExampleData = false;
        });
      } else {
        // 如果取消选择示例数据，清空输入框
        this.userInputIds = '';
        console.log('已取消示例数据选择，清空输入框');
      }
    },
    
    // 处理模式选择变化（确保排他性）
    handleModeChange(selectedMode) {
      console.log('模式选择变化:', selectedMode);
      // 由于使用复选框但需要排他性，直接设置选中的模式
      this.mode = selectedMode;
    },
    
    // 下载示例文件
    async downloadExampleFile(fileType) {
      try {
        if (fileType === 'xlsx') {
          // 对于Excel文件，调用后端API
          await this.downloadExcelFromAPI();
        } else {
          // 对于txt和csv文件，仍然使用前端生成的方式
          this.downloadTextFile(fileType);
        }
      } catch (error) {
        console.error('下载示例文件失败:', error);
        alert('下载文件失败，请重试');
      }
    },
    
    // 从后端API下载Excel文件
    async downloadExcelFromAPI() {
      try {
        const response = await axios.post('/api/session/download_example_data/', {
          module: 'ebio',
          type: 'excel'
        }, {
          responseType: 'blob', // 重要：设置响应类型为blob
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        // 创建下载链接
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'example.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('已从后端下载Excel示例文件');
      } catch (error) {
        console.error('从后端下载Excel文件失败:', error);
        if (error.response && error.response.data) {
          // 如果后端返回了错误信息
          const errorData = JSON.parse(new TextDecoder().decode(error.response.data));
          alert(`下载失败: ${errorData.message || '未知错误'}`);
        } else {
          alert('下载Excel文件失败，请重试');
        }
      }
    },
    
    // 下载文本文件（txt和csv）
    downloadTextFile(fileType) {
      try {
        let content = '';
        let filename = '';
        let mimeType = '';
        
        switch (fileType) {
          case 'txt':
            content = this.exampleDataIds.join('\n');
            filename = 'example.txt';
            mimeType = 'text/plain';
            break;
          case 'csv':
            // 使用 Excel 公式格式，防止自动转换日期
            const csvRows = this.exampleDataIds.map(id => `"=""${String(id).replace(/"/g, '""')}"""`);
            content = 'ID\n' + csvRows.join('\n');
            filename = 'example.csv';
            mimeType = 'text/csv';
            break;
          default:
            console.error('不支持的文件类型:', fileType);
            return;
        }
        
        // 创建Blob并下载
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log(`已下载示例文件: ${filename}`);
      } catch (error) {
        console.error('下载文本文件失败:', error);
        alert('下载文件失败，请重试');
      }
    },
    
    // 高亮搜索文本
    highlightText(text, searchTerm) {
      if (!text || !searchTerm || searchTerm.trim() === '') {
        return this.escapeHtml(text || '');
      }
      
      const escapedText = this.escapeHtml(text);
      const escapedSearchTerm = this.escapeHtml(searchTerm);
      
      // 使用正则表达式进行不区分大小写的匹配
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      
      return escapedText.replace(regex, '<mark class="search-highlight">$1</mark>');
    },
    
    // 转义HTML字符
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    
    // 添加步骤日志
    addStepLog(stepName, status) {
      // 通过ref调用 ToolsNavigationDrawer 的方法
      if (this.$refs.toolsDrawer) {
        this.$refs.toolsDrawer.addStepLog(stepName, status);
      }
    },
    
    // 添加自定义日志
    addCustomLog(message, status) {
      if (this.$refs.toolsDrawer) {
        this.$refs.toolsDrawer.addCustomLog(message, status);
      }
    },
    
    // 获取文件图标
    getFileIcon(fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      switch (extension) {
        case 'xlsx':
        case 'xls':
          return 'mdi-file-excel';
        case 'csv':
          return 'mdi-file-delimited';
        case 'txt':
          return 'mdi-file-document';
        default:
          return 'mdi-file';
      }
    },
    
    // 获取文件图标颜色
    getFileIconColor(fileType) {
      if (fileType && fileType.includes('excel')) return 'success';
      if (fileType && fileType.includes('csv')) return 'info';
      if (fileType && fileType.includes('text')) return 'warning';
      return 'primary';
    },
    
    // 获取文件类型文本
    getFileTypeText(fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      switch (extension) {
        case 'xlsx':
        case 'xls':
          return 'Excel 表格';
        case 'csv':
          return 'CSV 文件';
        case 'txt':
          return '文本文件';
        default:
          return '未知格式';
      }
    },
    
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async stepperNext() {
  if (this.isLastStep) {
    this.closeCurrentTab();
    return;
  }
  // 设置loading为true，表示开始加载
  this.loading = true;
  console.dir("Step: " + this.stepId)
  
  // 调试：检查当前文件状态
  console.log('=== 调试文件状态 ===');
  console.log('dataFileExisted:', this.dataFileExisted);
  console.log('dataFiles:', this.dataFiles);
  console.log('dataFiles类型:', typeof this.dataFiles);
  console.log('dataFiles是否为数组:', Array.isArray(this.dataFiles));
  console.log('fileInfoList:', this.fileInfoList);
  console.log('userInputIds:', this.userInputIds);
  console.log('useExampleData:', this.useExampleData);
  console.log('=====================')

  try {
    if (this.stepId == 1) {
      // 第一步：验证数据输入
      if (!this.hasValidInput) {
        this.loading = false;
        alert('请上传文件、输入词汇或选择示例数据后再继续');
        return;
      }
      // 第一步：处理上传的文件或示例数据
      try {
        let response;
        
        // 检查是否有上传文件
        if (this.dataFileExisted && this.dataFiles && this.dataFiles.length > 0) {
          console.log('处理上传的文件数量:', this.dataFiles.length);
          console.log('文件列表:', this.dataFiles.map(f => f.name));
          
          // 设置上传状态
          this.fileUploadStatus = 'uploading';
          
          // 检查session
          if (!this.sessionUuid) {
            console.error('缺少session UUID');
            this.fileUploadStatus = 'error';
            this.convertResult = { 
              success: false, 
              message: '缺少会话信息，请刷新页面重试',
              total_count: 0,
              converted_count: 0,
              processing_time: 0
            };
            return;
          }
          
          try {
            // 创建FormData对象，添加多个文件
            const formData = new FormData();
            formData.append('session', this.sessionUuid);
            
            // 添加所有文件到FormData
            this.dataFiles.forEach((file) => {
              formData.append('files', file); // 使用'files'字段名支持多文件
            });
            
            console.log('发送多文件上传请求，文件数量:', this.dataFiles.length);
            const uploadResponse = await axios.post('/api/session/uploadFile/', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            console.log('多文件上传响应:', uploadResponse.data);

            // 处理响应 - 新流程：uploadFile 只返回 ids，需要再调用 convert 接口
            if (uploadResponse.data.success) {
              const extractedIds = uploadResponse.data.ids;
              console.log('从文件中提取的ID数量:', extractedIds?.length || 0);

              if (!extractedIds || extractedIds.length === 0) {
                this.fileUploadStatus = 'error';
                this.convertResult = {
                  success: false,
                  message: '文件中未找到有效的ID',
                  total_count: 0,
                  converted_count: 0,
                  processing_time: 0
                };
              } else {
                // 调用 ebio/convert 接口进行ID转换
                const convertRequestData = {
                  ids: extractedIds,
                  session: this.sessionUuid
                };

                if (this.useExampleData) {
                  convertRequestData.type = 'EPPD';
                }

                console.log('发送ID转换请求:', convertRequestData);

                const convertResponse = await axios.post('/api/ebio/convert/', convertRequestData, {
                  headers: {
                    'Content-Type': 'application/json',
                  },
                });

                console.log('ID转换响应:', convertResponse.data);
                this.convertResult = convertResponse.data;

                if (this.convertResult.success) {
                  this.fileUploadStatus = 'success';
                  console.log('文件上传并转换成功');
                } else {
                  this.fileUploadStatus = 'error';
                }
              }
            } else {
              console.error('多文件上传失败:', uploadResponse.data.message);
              this.fileUploadStatus = 'error';
              this.convertResult = { success: false, message: uploadResponse.data.message || '多文件上传失败' };
            }
          } catch (uploadError) {
            console.error('多文件上传错误:', uploadError);
            this.fileUploadStatus = 'error';
            this.convertResult = {
              success: false,
              message: '文件上传或转换失败: ' + uploadError.message,
              total_count: 0,
              converted_count: 0,
              processing_time: 0
            };
          }
          
        } else if (this.userInputIds && this.userInputIds.trim()) {
          // 处理用户输入的词汇（包括示例数据填充的词汇）
          console.log('处理输入框中的词汇数据...');
          const ids = this.userInputIds.split('\n').map(id => id.trim()).filter(id => id.length > 0);
          console.log('解析后的词汇列表:', ids);
          
          const requestData = {
            "ids": ids,
            "session": this.sessionUuid
          };
          
          // 如果是示例数据，添加类型信息
          if (this.useExampleData) {
            requestData.type = 'EPPD'; // 统一使用EPPD类型
            console.log('发送示例数据转换请求:', requestData);
          } else {
            console.log('发送用户输入词汇转换请求:', requestData);
          }
          
          response = await axios.post('/api/ebio/convert/', requestData, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          this.convertResult = response.data;
        } else {
          // 没有任何数据输入
          console.warn('请上传文件、输入词汇或选择示例数据');
          this.convertResult = { 
            success: false, 
            message: '请上传文件、输入词汇或选择示例数据',
            total_count: 0,
            converted_count: 0,
            processing_time: 0
          };
        }
        
        console.log('转换结果:', this.convertResult);
        
        if (this.convertResult && this.convertResult.success) {
          // 检查转换的数据条数
          if (this.convertResult.converted_count === 0) {
            this.loading = false;
            alert('数据转换成功，但没有找到有效的ID。请检查输入的数据格式是否正确。');
            return;
          }
          
          console.log('转换成功，共转换', this.convertResult.converted_count, '个ID');
          // 加载转换后的数据到第二步的表格
          this.loadConvertedData();
          
          console.log(`数据转换成功，共处理 ${this.convertResult.converted_count} 个ID`);
          
          // 添加步骤成功日志
          this.addStepLog('Upload Data', 'success');
          
          // 只有转换成功且有数据时才跳转到下一步
          this.stepId++;
          await this.updateSessionStep();
        } else {
          console.error('数据转换失败: ' + (this.convertResult?.message || '未知错误'));
          this.loading = false;
          alert('数据转换失败: ' + (this.convertResult?.message || '未知错误') + '。请检查数据格式后重试。');
          return;
        }
      } catch (error) {
        console.error('API调用失败:', error);
        console.error('数据转换失败: ' + error.message);
        // 设置错误结果
        this.convertResult = { 
          success: false, 
          message: '请求失败: ' + error.message,
          total_count: 0,
          converted_count: 0,
          processing_time: 0
        };
      }
    } else if (this.stepId == 2) {
      console.log('进入第二步，处理tidy数据...');
      
      // 验证tidy数据是否准备好
      if (!this.vocaRowData || this.vocaRowData.length === 0) {
        console.warn('请先在第一步完成数据转换');
        this.loading = false;
        alert('请先在第一步完成数据转换');
        return;
      }
      
      // 检查是否有选中的数据
      const hasSelectedData = this.vocaRowData.some(row => row.selected === true);
      if (!hasSelectedData) {
        console.warn('请至少选择一条数据进行处理');
        this.loading = false;
        alert('请至少选择一条数据进行处理');
        return;
      }
      
      try {
        // 获取选中的数据行，使用 item.selected 字段
        const selectedData = this.vocaRowData.filter(row => row.selected === true);
        
        console.log('选中的数据行数:', selectedData.length);
        console.log('选中的数据:', selectedData);
        
        // 构造发送给后端的数据格式
        const tidyData = selectedData.map(row => ({
          input_id: row.input_id || '',
          exid: row.exid || '',
          preferred_name: row.preferred_name || '',
          id_info: row.id_info || '',
          synonyms: row.synonyms || ''
        }));
        
        const requestData = {
          session: this.sessionUuid,
          data: tidyData
        };
        
        console.log('发送Tidy数据请求:', requestData);
        
        // 发送到TidyData接口
        const tidyResponse = await axios.post('/api/ebio/TidyData/', requestData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        console.log('TidyData响应:', tidyResponse.data);
        
        // 处理响应
        if (tidyResponse.data.success) {
          // 如果后端返回了 tidy data，确保每个对象都有 selected: false
          if (tidyResponse.data.data && Array.isArray(tidyResponse.data.data)) {
            tidyResponse.data.data = tidyResponse.data.data.map(item => ({
              ...item,
              selected: false   // ✅ 每行有自己的 selected
            }));
          }
          
          this.convertResult = {
            success: true,
            message: tidyResponse.data.message,
            file_info: tidyResponse.data.file_info,
            processing_time: tidyResponse.data.processing_time,
            total_count: tidyData.length,
            converted_count: tidyData.length
          };
          console.log('Tidy数据处理成功');
          
          // 添加步骤成功日志
          this.addStepLog('Tidy Data', 'success');
          
          // 只有处理成功时才跳转到下一步
          this.stepId++;
          await this.updateSessionStep();
        } else {
          this.convertResult = { 
            success: false, 
            message: tidyResponse.data.message || 'Tidy数据处理失败',
            total_count: tidyData.length,
            converted_count: 0,
            processing_time: tidyResponse.data.processing_time || 0
          };
          this.loading = false;
          alert('Tidy数据处理失败: ' + (tidyResponse.data.message || '未知错误'));
          return;
        }
        
      } catch (error) {
        console.error('TidyData API调用失败:', error);
        this.convertResult = { 
          success: false, 
          message: 'TidyData处理失败: ' + error.message,
          total_count: 0,
          converted_count: 0,
          processing_time: 0
        };
        this.loading = false;
        alert('TidyData处理失败: ' + error.message);
        return;
      }
      
    } else if (this.stepId == 3) {
      // Step 3: 检查是否可以进入第四步
      console.log('Entering Step 3, checking if can proceed to Step 4');
      
      // 如果会话状态已经是1（已完成），或者有biolink结果，允许进入第四步
      if (this.isSessionCompleted || (this.biolinkResult && this.biolinkResult.success)) {
        console.log('可以进入第四步，跳转中...');
        this.stepId++;
        await this.updateSessionStep();
      } else {
        console.log('第三步需要先构建模型才能进入第四步');
        this.loading = false;
        alert('请先点击Transform按钮构建模型后再继续');
        return;
      }
      
    }

    // 跳转逻辑现在在各个步骤的验证成功后进行

  } catch (error) {
    console.log("请求失败", error);
    // 处理失败的情况，可以通过提示用户重新尝试
  } finally {
    // 请求完成后，无论成功还是失败，都要关闭loading状态
    this.loading = false;
  }
},

    closeCurrentTab() {
      // window.close() 只能关闭由脚本打开的窗口，使用路由导航代替
      this.$router.push('/');
    },
    stepperPre() {
      console.log('返回上一步，当前步骤:', this.stepId, '->', this.stepId - 1);
      this.stepId--;
      
      // 如果从第四步返回，保留数据以便再次进入时快速显示
      if (this.stepId === 3) {
        console.log('从第四步返回，保留文件结构数据');
      }
    },

    // 更新会话步骤
    async updateSessionStep() {
      try {
        console.log('更新会话步骤到:', this.stepId);
        
        // 检查是否有活动会话
        if (!this.sessionUuid) {
          console.warn('没有活动会话，跳过会话更新');
          return;
        }

        // 确定是否是最后一步，如果是Summary页面（第4步），则设置status为1
        const isLastStep = this.stepId === 4;
        const status = isLastStep ? 1 : 0;

        await this.updateSession({
          session_step: this.stepId,
          status: status
        });

        console.log(`会话步骤更新成功: step=${this.stepId}, status=${status}`);
        
        if (isLastStep) {
          console.log('已到达最后一步，会话状态设为完成');
        }

      } catch (error) {
        console.error('更新会话步骤失败:', error);
        // 不阻断用户操作，只是记录错误
        // 可以选择显示一个非阻塞的警告消息
        if (error.message !== '没有活动的会话') {
          console.warn('会话更新失败，但不影响继续操作');
        }
      }
    },

    // 创建新会话
    async createNewSession() {
      try {
        console.log('开始创建新会话...');
        
        // 检查用户是否已登录
        const access_token = localStorage.getItem('access_token');
        if (!access_token) {
          throw new Error('用户未登录');
        }
        
        // 调用 Vuex action 创建会话
        const sessionData = await this.createSession({
          module_name: 'ebio'
        });
        
        console.log('新会话创建成功:', sessionData);
        return sessionData;
        
      } catch (error) {
        console.error('创建新会话失败:', error);
        throw error;
      }
    },

    // 初始化会话
    async initializeSession() {
      try {
        if (this.sessionUuid) {
          console.log('初始化会话，检查当前状态...');
          
          // 获取当前会话信息，而不是总是重置为步骤1
          try {
            const access_token = localStorage.getItem('access_token');
            if (access_token) {
              const response = await axios.post('/api/session/GetSessionInfo/', {
                access_token: access_token,
                session: this.sessionUuid
              });
              
              if (response.data.code === "200") {
                const sessionInfo = response.data.data;
                console.log('获取到会话信息:', sessionInfo);
                
                // 如果会话已经完成（状态为1），保持当前状态
                if (sessionInfo.status === 1) {
                  console.log('会话已完成，保持当前状态');
                  // 更新store中的会话状态
                  this.$store.commit('SET_SESSION_STATUS', 1);
                } else {
                  // 如果会话未完成，重置为步骤1
                  console.log('会话未完成，重置为步骤1');
                  await this.updateSession({
                    session_step: 1,
                    status: 0
                  });
                }
              } else {
                // 如果获取会话信息失败，重置为步骤1
                console.log('获取会话信息失败，重置为步骤1');
                await this.updateSession({
                  session_step: 1,
                  status: 0
                });
              }
            }
          } catch (sessionError) {
            console.warn('获取会话信息失败，重置为步骤1:', sessionError);
            await this.updateSession({
              session_step: 1,
              status: 0
            });
          }
          
          console.log('会话初始化完成');
        } else {
          console.warn('没有活动会话UUID，跳过初始化');
        }
      } catch (error) {
        console.error('初始化会话失败:', error);
        // 不阻断页面加载
      }
    },

    onSelectionChanged(selectedIds) {
      // 防止递归更新
      if (!selectedIds || !Array.isArray(selectedIds)) {
        return;
      }

      console.log('选择变化:', selectedIds);

      // 更新每行的 selected 状态
      this.vocaRowData.forEach((item) => {
        item.selected = selectedIds.includes(item.id);
      });

      console.log('更新后的 vocaRowData selected 状态:', this.vocaRowData.map(item => item.selected));
    },

    downloadTidyData() {
      if (!this.selected || this.selected.length === 0) return;

      // 获取选中的行数据
      const selectedSet = new Set(this.selected);
      const selectedRows = this.vocaRowData.filter(row => selectedSet.has(row.id));
      if (!selectedRows.length) return;

      const headers = this.vocaHeaders.map(header => header.key);
      const csvLines = [];
      csvLines.push(headers.join(','));

      selectedRows.forEach(row => {
        const line = headers.map(key => {
          let value = row[key] ?? '';
          // Input ID 列使用 Excel 公式格式，防止自动转换日期
          if (key === 'input_id') {
            const escaped = String(value).replace(/"/g, '""');
            return `"=""${escaped}"""`;
          }
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',');
        csvLines.push(line);
      });

      const csvContent = csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const sessionStr = this.sessionUuid ? `_${this.sessionUuid.substring(0, 8)}` : '';
      link.href = url;
      link.download = `tidy_data${sessionStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },

    async doAllTable() {
      var vocaRsp = await this.doTable("ExpoVoca.csv")
      if (!vocaRsp || !vocaRsp.header) {
        console.error('Failed to load voca data')
        return
      }
      
      var vocaHeaders = vocaRsp["header"]

      // 交换 id_info 和 synonyms 列的位置
      const idInfoIndex = vocaHeaders.findIndex(header => header.title === 'id_info');
      const synonymsIndex = vocaHeaders.findIndex(header => header.title === 'synonyms');
      
      if (idInfoIndex !== -1 && synonymsIndex !== -1) {
        // 交换两个列的位置
        [vocaHeaders[idInfoIndex], vocaHeaders[synonymsIndex]] = [vocaHeaders[synonymsIndex], vocaHeaders[idInfoIndex]];
        console.log('在 doAllTable 中已交换 id_info 和 synonyms 列的位置');
      }

      for (let i in vocaHeaders) {
        let vocaHeader = {
          align: '',
          key: '',
          sortable: true,
          title: '',
        }
        vocaHeader.key = vocaHeaders[i].title
        vocaHeader.title = vocaHeaders[i].title
        if (vocaHeader.key === 'SerialNo') {
          vocaHeader.align = 'start'
        }
        this.vocaHeaders.push(vocaHeader)
      }

      let rowAttrs = []
      for (let i in this.vocaHeaders) {
        rowAttrs.push(this.vocaHeaders[i].key)
      }
      console.log(rowAttrs)
      this.vocaRowData = vocaRsp.node
      var dataRsp = await this.doTable("ExpoData.csv")
      this.dataHeaders = dataRsp["header"]
      console.log('dataHeaders', dataRsp["header"])
      this.dataRowData = dataRsp["node"]
      console.log('dataRowData', this.dataRowData)
      this.roundToThreeDecimals()
      console.log('After dataRowData', this.dataRowData)
    },
    async doTable(filename) {
      let data = JSON.stringify({
        "path": "demo/",
        "filename": filename
      });
      try {
        const response = await axios.post('/exposome/getdemofile', data, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        var rsp = this.$globalParseCsvData(response.data)
        return rsp
      } catch (error) {
        console.log(error)
      }
    },
    selectAllByDefault() {
      // 只选择有完整数据的行（即除了 input_id 之外还有其他列数据的行）
      this.selected = this.vocaRowData
        .filter(item => {
          // 检查除了 input_id 和 id、selected 之外的其他列是否有非空数据
          const hasOtherData = Object.keys(item).some(key => {
            if (key === 'input_id' || key === 'id' || key === 'selected') return false;
            const value = item[key];
            return value !== null && value !== undefined && value !== '' && value !== 'N/A';
          });
          return hasOtherData;
        })
        .map(item => item.id);

      // 同时更新每行的 selected 状态
      this.vocaRowData.forEach((item) => {
        // 检查除了 input_id 和 id、selected 之外的其他列是否有非空数据
        const hasOtherData = Object.keys(item).some(key => {
          if (key === 'input_id' || key === 'id' || key === 'selected') return false;
          const value = item[key];
          return value !== null && value !== undefined && value !== '' && value !== 'N/A';
        });
        item.selected = hasOtherData;
      });

      console.log('默认选择完成，selected 数组:', this.selected);
      console.log('每行 selected 状态:', this.vocaRowData.map(item => item.selected));
    },
    roundToThreeDecimals() {
  this.dataRowData = this.dataRowData.map(row => {
    const updatedRow = { ...row };
    Object.keys(updatedRow).forEach(key => {
      // 检查是否为数字或可以解析为数字
      if (typeof updatedRow[key] === 'string' && !isNaN(Number(updatedRow[key]))) {
        // 解析字符串为数字
        const parsedNumber = parseFloat(updatedRow[key]);
        updatedRow[key] = parseFloat(parsedNumber.toFixed(3));
      } else if (typeof updatedRow[key] === 'number') {
        // 直接处理数字
        updatedRow[key] = parseFloat(updatedRow[key].toFixed(3));
      }
    });
    return updatedRow;
  });
},

loadConvertedData() {
  console.log('开始加载转换数据...');
  console.log('convertResult:', this.convertResult);
  
  // 处理转换后的数据
  if (!this.convertResult || !this.convertResult.data) {
    console.error('没有转换数据可用');
    return;
  }

  const convertedData = this.convertResult.data;
  console.log('转换数据:', convertedData);
  
  // 清空现有数据
  this.vocaRowData = [];
  this.dataRowData = [];
  this.vocaHeaders = [];
  this.dataColumnDefs = [];
  
  // 处理词汇表数据 - 直接使用接口返回的数据和列名
  if (convertedData && convertedData.length > 0) {
    // 从第一条数据中获取所有列名
    const firstItem = convertedData[0];
    let columnKeys = Object.keys(firstItem);
    
    // 交换 id_info 和 synonyms 列的位置
    const idInfoIndex = columnKeys.indexOf('id_info');
    const synonymsIndex = columnKeys.indexOf('synonyms');
    
    if (idInfoIndex !== -1 && synonymsIndex !== -1) {
      // 交换两个列的位置
      [columnKeys[idInfoIndex], columnKeys[synonymsIndex]] = [columnKeys[synonymsIndex], columnKeys[idInfoIndex]];
      console.log('已交换 id_info 和 synonyms 列的位置');
    }
    
    // 生成表头
    this.vocaHeaders = columnKeys.map(key => ({
      title: key,
      key: key,
      align: '',
      sortable: true
    }));
    
    // 确保复选框列在第一列（Vuetify 的 show-select 会自动处理）
    console.log('表头配置完成，复选框列将自动显示在第一列');
    
    console.log('设置表头:', this.vocaHeaders);
    console.log('表头数量:', this.vocaHeaders.length);
    console.log('表头键名:', this.vocaHeaders.map(h => h.key));
    
    // 为每行数据添加 id 和 selected 字段
    // 只有 input_id 有数据的行默认不选中，其他列有数据的行默认选中
    this.vocaRowData = convertedData.map((item, index) => {
      // 检查除了 input_id 之外的其他列是否有非空数据
      const hasOtherData = Object.keys(item).some(key => {
        if (key === 'input_id') return false;
        const value = item[key];
        return value !== null && value !== undefined && value !== '' && value !== 'N/A';
      });

      return {
        ...item,
        id: `row_${index}`,  // 为每行添加唯一ID
        selected: hasOtherData  // 只有当其他列有数据时才默认选中
      };
    });
  } else {
    this.vocaHeaders = [];
    this.vocaRowData = [];
  }
  
  console.log('转换后的行数据:', this.vocaRowData);
  
  // 设置数据表格的列定义 - 使用动态列
  this.dataColumnDefs = this.vocaHeaders.map(header => ({
    title: header.title,
    key: header.key
  }));
  
  // 设置数据行数据
  this.dataRowData = this.vocaRowData;
  
  console.log('转换数据加载完成');
  console.log('vocaRowData长度:', this.vocaRowData.length);
  console.log('dataRowData长度:', this.dataRowData.length);
  
  // 默认选择所有行
  this.selectAllByDefault();

  // 初始化排序 - 默认第一列升序
  if (this.vocaHeaders.length > 0) {
    const firstKey = this.vocaHeaders[0].key;
    this.vocaSortBy = [{ key: firstKey, order: 'asc' }];
    this.lastSortColumn = firstKey;  // 同时设置 lastSortColumn，防止 watch 触发改变
    console.log('初始化排序:', this.vocaSortBy);
  }

  // 确保DOM更新
  this.$nextTick(() => {
    console.log('DOM更新完成，检查表格数据...');
    console.log('当前vocaRowData:', this.vocaRowData);
  });
},

processBiolinkData() {
  // 这个方法现在只用于Summary页面显示，不处理网络图数据
  if (!this.biolinkResult || !this.biolinkResult.success) {
    console.error('没有有效的biolink数据');
    return;
  }

  console.log('Biolink模型构建成功，文件信息:', this.biolinkResult.file_info);
  
  // 只设置基本状态，实际的网络图数据处理在ebioChart.vue中进行
  this.nodeLoad = true;
},

renderNetworkGraph() {
  // 检查是否有echarts
  if (typeof this.$echarts === 'undefined') {
    console.error('ECharts未加载');
    return;
  }

  // 等待DOM更新完成
  this.$nextTick(() => {
    const chartDom = document.getElementById('network-graph');
    if (!chartDom) {
      console.error('Network graph container not found');
      return;
    }

    const myChart = this.$echarts.init(chartDom);
    
    // 准备节点数据
    const nodes = this.processedNodes.map(node => ({
      id: node.EXID,
      name: node.EXID,
      label: node['Preferred name'],
      category: node['Group name'],
      symbolSize: this.nodeType == 1 ? this.nodevSize : Math.min(10 + (node.Degree || 0) * 2, 30),
      value: node.Degree || 0
    }));

    // 准备边数据
    const edges = this.processedEdges.map(edge => ({
      source: edge.source,
      target: edge.target,
      value: edge.interaction
    }));

    // 获取唯一的类别并设置符号
    const uniqueCategories = [...new Set(nodes.map(node => node.category))];
    const categories = uniqueCategories.map(category => {
      let symbol = 'circle';
      if (category.includes('(prot)') || category.includes('（prot）')) {
        symbol = 'triangle';
      } else if (category.includes('(met)') || category.includes('（met）')) {
        symbol = 'diamond';
      }
      return { name: category, symbol: symbol };
    });

    // 设置颜色
    const colors = this.colorList.slice(0, categories.length);

    this.option = {
      title: {
        text: 'Biolink Network Graph',
        subtext: this.biolinkResult?.message || 'Network visualization',
        top: 'top',
        left: 'center'
      },
      tooltip: {
        formatter: function(params) {
          if (params.dataType === 'node') {
            return `Node: ${params.data.label}<br/>Group: ${params.data.category}<br/>Degree: ${params.data.value}`;
          } else {
            return `Edge: ${params.data.source} → ${params.data.target}<br/>Interaction: ${params.data.value}`;
          }
        }
      },
      legend: {
        data: categories.map(cat => ({ name: cat.name, icon: cat.symbol })),
        orient: 'vertical',
        left: 'left',
        top: 'middle'
      },
      animationDurationUpdate: 1500,
      animationEasingUpdate: 'quinticInOut',
      series: [{
        type: 'graph',
        layout: 'force',
        data: nodes,
        links: edges,
        categories: categories,
        roam: true,
        label: {
          show: true,
          position: 'right',
          fontSize: this.frontSize,
          formatter: ((params) => {
            if (this.nodeNameType == "1") {
              return `${params.data.name}`;
            } else {
              return `${params.data.label}`;
            }
          })
        },
        force: {
          repulsion: 100,
          edgeLength: 200
        },
        lineStyle: {
          color: this.linecolor,
          width: this.calculateY(this.edgeSize),
          opacity: 1 - this.lineTransparency / 100
        },
        emphasis: {
          focus: 'adjacency',
          scale: 3
        }
      }],
      color: colors
    };

    myChart.setOption(this.option);
    
    // 保存图表实例以便后续操作
    this.networkChart = myChart;
    
    // 添加事件监听器
    myChart.on('click', (params) => {
      if (params.dataType === 'node') {
        console.log(`点击了节点: ${params.data.label}`);
      } else if (params.dataType === 'edge') {
        console.log(`点击了边: ${params.data.source} → ${params.data.target}`);
      }
    });
  });
},

handleResize() {
  // 调整网络图的尺寸
  this.$nextTick(() => {
    if (this.networkChart) {
      this.networkChart.resize();
    }
  });
},

// Layout control methods
changeSlider(val) {
  if (this.nodeType == 1) {
    this.option.series[0].symbolSize = val;
    this.$nextTick(() => {
      let myChart = this.$echarts.init(document.getElementById('network-graph'));
      if (myChart) {
        myChart.setOption(this.option, true);
      }
    });
  }
  if (this.nodeType == 2) {
    this.nodeScaleFactor = val / 10;
    this.readSymbolSize();
    this.$nextTick(() => {
      let myChart = this.$echarts.init(document.getElementById('network-graph'));
      if (myChart) {
        myChart.setOption(this.option, true);
      }
    });
  }
},

changeNodeNameType(val) {
  console.log(val);
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

changeEdgeSlider(val) {
  this.option.series[0].lineStyle.width = this.calculateY(val);
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

changeLabelSlider(val) {
  this.option.series[0].label.fontSize = val;
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

changeNodeTransparency(val) {
  for (var i = 0; i < this.colorList.length; i++) {
    this.colorList[i] = chroma(this.colorList[i]).alpha(1 - val / 100).css('rgba');
  }
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

changeLineTransparency(val) {
  this.option.series[0].lineStyle.opacity = 1 - val / 100;
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

lineColorChange(val) {
  this.option.series[0].lineStyle.color = val;
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

changeNodeColorList(color, index) {
  this.colorList[index] = color;
  this.option.color = this.colorList;
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.setOption(this.option, true);
    }
  });
},

saveViewSvg() {
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      var svgDataString = myChart.renderToSVGString();
          
      if (!svgDataString.startsWith('<svg')) {
        svgDataString = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400">${svgDataString}</svg>`;
      }
          
      var svgDataUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgDataString);
      var a = document.createElement('a');
      a.href = svgDataUrl;
      a.download = 'chart.svg';
      a.click();
    }
  });
},

saveView() {
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      var img = new Image();
      img.src = myChart.getDataURL({
        type: "png",
        pixelRatio: 2,
        backgroundColor: "#fff",
      });
      img.onload = function () {
        var canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        var dataURL = canvas.toDataURL("image/png");
        var a = document.createElement("a");
        var event = new MouseEvent("click");
        a.download = "pic.png";
        a.href = dataURL;
        a.dispatchEvent(event);
        a.remove();
      };
    }
  });
},

restoreView() {
  this.$nextTick(() => {
    let myChart = this.$echarts.init(document.getElementById('network-graph'));
    if (myChart) {
      myChart.dispatchAction({
        type: 'restore'
      });
      this.restoreFigure();
    }
  });
},

calculateY(x) {
  if (x >= 0 && x <= 10) {
    return (1 / 100) * Math.pow(x, 2);
  } else if (x > 10 && x <= 20) {
    return (2 / 5) * x - 3;
  } else {
    return 0;
  }
},



readSymbolSize() {
  for (var i = 0; i < this.processedNodes.length; i++) {
    var size = 1;
    for (var j = 0; j < this.processedEdges.length; j++) {
      if (this.processedEdges[j]['source'].includes(this.processedNodes[i]['EXID']) || 
          this.processedEdges[j]['target'].includes(this.processedNodes[i]['EXID'])) {
        size = size + 1;
      }
    }
    this.processedNodes[i]['symbolSize'] = size * this.nodeScaleFactor;
    this.processedNodes[i]['value'] = size;
    if (this.nodeType == "1") {
      delete this.processedNodes[i].symbolSize;
      delete this.processedNodes[i].value;
    }
  }
},

restoreFigure() {
  this.readSymbolSize();
  // 网络图相关操作已移至ebioChart.vue
},

setCategories() {
  this.categories = [];
  const uniqueGroups = [...new Set(this.processedNodes.map(node => node['Group name']))];
  
  uniqueGroups.forEach(groupName => {
    this.categories.push({
      name: groupName,
      symbol: 'circle'
    });
  });
  
  console.log('设置的categories:', this.categories);
},

// 转换数据方法
async transformData() {
  try {
    console.log('开始构建Biolink模型，模式:', this.mode);

    // 检查session
    if (!this.sessionUuid) {
      console.error('缺少session UUID');
      this.$message({
        message: 'Missing session information, please refresh the page and try again',
        type: 'warning',
        duration: 4000
      });
      return;
    }

    // ✅ 关键:在用户点击Run按钮时立即打开窗口(同步操作,不会被拦截)
    // 先打开一个loading页面,等数据准备好后再跳转到实际的图表页面
    console.log('预先打开图表窗口');
    this.preOpenedChartWindow = window.open('/loading.html', '_blank');

    // 如果窗口被拦截,preOpenedChartWindow会是null
    if (!this.preOpenedChartWindow) {
      console.warn('弹出窗口被浏览器拦截');
      this.$message({
        message: 'Popup blocked by browser. Please allow popups for this site.',
        type: 'warning',
        duration: 4000
      });
    } else {
      // 在新窗口中显示loading信息
      this.preOpenedChartWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Loading Network Visualization...</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                background: #E6ECF7;
                color: #2c3e50;
              }
              .loader-container {
                text-align: center;
                padding: 40px;
              }
              .loader {
                border: 5px solid rgba(44, 62, 80, 0.1);
                border-radius: 50%;
                border-top: 5px solid #4a90e2;
                width: 60px;
                height: 60px;
                animation: spin 1s linear infinite;
                margin: 0 auto 30px;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              h2 {
                margin: 0 0 15px;
                font-size: 28px;
                font-weight: 600;
                color: #2c3e50;
              }
              p {
                margin: 0;
                font-size: 16px;
                color: #5a6c7d;
                line-height: 1.6;
              }
              .status {
                margin-top: 20px;
                padding: 15px 25px;
                background: rgba(74, 144, 226, 0.1);
                border-radius: 8px;
                font-size: 14px;
                color: #4a90e2;
              }
            </style>
          </head>
          <body>
            <div class="loader-container">
              <div class="loader"></div>
              <h2>Building Network Model...</h2>
              <p>Please wait while we prepare your visualization</p>
              <div class="status">This may take a few minutes</div>
            </div>
          </body>
        </html>
      `);
      this.preOpenedChartWindow.document.close();
    }

    // 设置等待状态
    this.biolinkLoading = true;
    this.overlay = true;  // 显示灰色蒙皮，阻止用户操作

    // 构造请求数据
    const requestData = {
      mode: this.mode,
      session: this.sessionUuid
    };

    console.log('发送Biolink构建请求:', requestData);

    // 发送到buildModel接口，立即返回task_id
    const response = await axios.post('/api/ebio/BuildModel/', requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('BuildModel响应:', response.data);

    // 检查初始响应
    if (response.data.success && response.data.task_id) {
      const taskId = response.data.task_id;
      console.log('任务已创建，task_id:', taskId);

      // 显示任务创建消息
      this.$message({
        message: 'Model build task created, processing...',
        type: 'info',
        duration: 2000
      });

      // 开始轮询任务状态
      const result = await this.pollTaskStatus(taskId);

      if (result.success) {
        // 任务成功完成
        this.biolinkResult = result.data;
        console.log('Biolink model build successful');

        // 处理基本状态信息用于Summary页面显示
        if (result.data.data) {
          this.processBiolinkData();
        }

        // Check if data size exceeds visualization limits
        // Data is in result.data.data (from /api/ebio/BuildTaskStatus/ response)
        const buildResult = result.data.data || {};
        const nodesCount = buildResult.nodes_count || 0;
        const edgesCount = buildResult.edges_count || 0;
        const maxNodes = 2500;  // TODO: Change back to 2500 after testing
        const maxEdges = 10000;  // TODO: Change back to 10000 after testing

        console.log(`Build result: ${nodesCount} nodes, ${edgesCount} edges (max: ${maxNodes} nodes, ${maxEdges} edges)`);

        if (nodesCount > maxNodes || edgesCount > maxEdges) {
          // Data too large for visualization - update the pre-opened window with warning
          console.log(`Data exceeds visualization limits: nodes=${nodesCount}, edges=${edgesCount}`);

          // Update the pre-opened window to show warning message
          if (this.preOpenedChartWindow && !this.preOpenedChartWindow.closed) {
            this.preOpenedChartWindow.document.open();
            this.preOpenedChartWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>Data Too Large</title>
                  <style>
                    body {
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      min-height: 100vh;
                      margin: 0;
                      background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
                    }
                    .warning-container {
                      text-align: center;
                      padding: 40px;
                      background: white;
                      border-radius: 16px;
                      box-shadow: 0 10px 40px rgba(0,0,0,0.1);
                      max-width: 500px;
                    }
                    .warning-icon {
                      font-size: 64px;
                      margin-bottom: 20px;
                    }
                    h2 {
                      color: #856404;
                      margin: 0 0 15px 0;
                      font-size: 24px;
                    }
                    .stats {
                      background: #fff3cd;
                      padding: 15px 20px;
                      border-radius: 8px;
                      margin: 20px 0;
                      font-size: 16px;
                      color: #856404;
                    }
                    .stats strong {
                      color: #664d03;
                    }
                    p {
                      color: #666;
                      line-height: 1.6;
                      margin: 15px 0;
                    }
                    .hint {
                      margin-top: 25px;
                      padding: 15px;
                      background: #e8f4fd;
                      border-radius: 8px;
                      color: #0c5460;
                      font-size: 14px;
                    }
                  </style>
                </head>
                <body>
                  <div class="warning-container">
                    <div class="warning-icon">⚠️</div>
                    <h2>Data Too Large for Visualization</h2>
                    <div class="stats">
                      <strong>${nodesCount}</strong> nodes, <strong>${edgesCount}</strong> edges<br>
                      <small>Maximum: ${maxNodes} nodes or ${maxEdges} edges</small>
                    </div>
                    <p>The model was built successfully, but the result is too large to display in the browser visualization.</p>
                    <div class="hint">
                      💡 Please download the data from your <strong>Operation History</strong> to view the complete results.
                    </div>
                  </div>
                </body>
              </html>
            `);
            this.preOpenedChartWindow.document.close();
          }
        } else {
          // Data size is within limits - proceed with visualization
          // 显示成功消息
          this.$message({
            message: 'Model build successful! Opening network visualization...',
            type: 'success',
            duration: 3000
          });

          // 调用showModelGraph接口获取图数据并在新标签页打开
          await this.openChartWithGraphData();
        }

        // 添加步骤成功日志
        this.addStepLog('Build Model', 'success');

        // 记录成功运行的模型，用于后续比较
        this.lastSuccessfulModel = this.mode;
        console.log('模型运行成功，记录模型:', this.mode);

        // 自动跳转到Summary页面（第4步）
        this.stepId = 4;

        // 更新会话步骤
        await this.updateSessionStep();


      } else {
        // 任务失败
        console.error('Biolink模型构建失败:', result.message);
        this.$message({
          message: `Build failed: ${result.message}`,
          type: 'error',
          duration: 4000
        });
        this.biolinkResult = {
          success: false,
          message: result.message,
          processing_time: 0
        };

        // 关闭预先打开的窗口
        if (this.preOpenedChartWindow && !this.preOpenedChartWindow.closed) {
          this.preOpenedChartWindow.close();
          this.preOpenedChartWindow = null;
        }
      }
    } else {
      console.error('创建任务失败:', response.data.message);
      this.$message({
        message: `Failed to create build task: ${response.data.message}`,
        type: 'error',
        duration: 4000
      });

      // 关闭预先打开的窗口
      if (this.preOpenedChartWindow && !this.preOpenedChartWindow.closed) {
        this.preOpenedChartWindow.close();
        this.preOpenedChartWindow = null;
      }
    }

  } catch (error) {
    console.error('BuildModel API调用失败:', error);
    this.$message({
      message: `Network model build failed: ${error.message}`,
      type: 'error',
      duration: 4000
    });
    this.biolinkResult = {
      success: false,
      message: 'API call failed: ' + error.message,
      processing_time: 0
    };

    // 关闭预先打开的窗口
    if (this.preOpenedChartWindow && !this.preOpenedChartWindow.closed) {
      this.preOpenedChartWindow.close();
      this.preOpenedChartWindow = null;
    }
  } finally {
    // 无论成功还是失败，都要关闭等待状态
    this.biolinkLoading = false;
    this.overlay = false;  // 隐藏灰色蒙皮，恢复用户操作
  }
},

// 轮询任务状态方法
async pollTaskStatus(taskId) {
  const maxTimeout = 600000; // 10分钟超时
  const pollInterval = 1000; // 1秒轮询间隔
  const startTime = Date.now();

  console.log('开始轮询任务状态，task_id:', taskId);

  while (Date.now() - startTime < maxTimeout) {
    try {
      const response = await axios.get(`/api/ebio/BuildTaskStatus/?task_id=${taskId}`);
      console.log('任务状态查询响应:', response.data);

      if (response.data.success) {
        const status = response.data.status;

        if (status === 'completed') {
          console.log('任务完成成功');
          return {
            success: true,
            data: response.data
          };
        } else if (status === 'failed') {
          console.log('任务执行失败:', response.data.message);
          return {
            success: false,
            message: response.data.message || '任务执行失败'
          };
        } else if (status === 'processing') {
          console.log('任务处理中，继续轮询...');
          // 继续轮询
        } else {
          console.log('未知任务状态:', status);
        }
      } else {
        if (response.data.status === 'not_found') {
          console.log('任务不存在或已过期');
          return {
            success: false,
            message: '任务不存在或已过期'
          };
        } else {
          console.log('查询任务状态失败:', response.data.message);
        }
      }

      // 等待下一次轮询
      await new Promise(resolve => setTimeout(resolve, pollInterval));

    } catch (error) {
      console.error('轮询任务状态出错:', error);
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  // 超时
  console.log('任务执行超时（10分钟）');
  return {
    success: false,
    message: 'Task timeout after 10 minutes'
  };
},

// 调用showModelGraph并打开图表页面
async openChartWithGraphData() {
  try {
    console.log('调用showModelGraph接口获取图数据');

    // 构造showModelGraph请求数据
    const payload = {
      session: this.sessionUuid,
      mode: this.mode,
      data_type: 'nodes' // 或者其他需要的参数
    };

    // 构建请求头，包含用户认证信息
    const headers = {
      'Content-Type': 'application/json',
    };

    const access_token = localStorage.getItem('access_token');
    if (access_token) {
      headers['Authorization'] = `Bearer ${access_token}`;
    }

    // 调用showModelGraph接口
    const response = await axios.post('/api/ebio/showModelGraph/', payload, {
      headers: headers
    });

    // 构建图表页面URL
    const chartUrl = `/views/ebio/ebioChart?session=${this.sessionUuid}&mode=${this.mode}`;

    if (response.data.success) {
      console.log('showModelGraph调用成功');
    } else {
      console.error('showModelGraph调用失败:', response.data.message);
      // 注意: 不显示错误消息,因为窗口会正常打开,图表页面会自己处理数据加载
    }

    // ✅ 使用预先打开的窗口并跳转到图表页面
    if (this.preOpenedChartWindow && !this.preOpenedChartWindow.closed) {
      console.log('更新预先打开的窗口URL');
      this.preOpenedChartWindow.location.href = chartUrl;
    } else {
      // 如果预先打开的窗口不存在或已关闭,尝试重新打开
      console.log('预先打开的窗口不可用,尝试重新打开');
      window.open(chartUrl, '_blank');
    }

  } catch (error) {
    console.error('调用showModelGraph出错:', error);
    // 注意: 不显示错误消息,因为窗口会正常打开,图表页面会自己处理数据加载

    // 即使出错，仍然使用预先打开的窗口
    const chartUrl = `/views/ebio/ebioChart?session=${this.sessionUuid}&mode=${this.mode}`;
    if (this.preOpenedChartWindow && !this.preOpenedChartWindow.closed) {
      this.preOpenedChartWindow.location.href = chartUrl;
    } else {
      window.open(chartUrl, '_blank');
    }
  }
},

// Summary 页面相关方法
downloadSummary() {
  try {
    const summaryData = {
      sessionInfo: {
        sessionUuid: this.sessionUuid || 'N/A',
        sessionName: this.sessionName || 'N/A',
        analysisMode: this.mode,
        dataType: this.useExampleData ? 'Example Data' : 'User Input',
        timestamp: new Date().toISOString()
      },
      dataProcessing: this.convertResult || {},
      networkAnalysis: {
        totalNodes: this.processedNodes.length,
        totalEdges: this.processedEdges.length,
        categories: this.categories.map(cat => cat.name)
      }
    };

    const dataStr = JSON.stringify(summaryData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ebio_summary_${this.sessionUuid || 'session'}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    console.log('Summary downloaded successfully');
  } catch (error) {
    console.error('Failed to download summary:', error);
    alert('下载摘要失败');
  }
},

exportResults() {
  try {
    // 创建包含所有结果的数据对象
    const exportData = {
      metadata: {
        sessionUuid: this.sessionUuid || 'N/A',
        sessionName: this.sessionName || 'N/A',
        analysisMode: this.mode,
        dataType: this.useExampleData ? 'Example Data' : 'User Input',
        exportTime: new Date().toISOString()
      },
      convertedData: this.vocaRowData,
      networkNodes: this.processedNodes,
      networkEdges: this.processedEdges,
      categories: this.categories
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ebio_results_${this.sessionUuid || 'session'}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    console.log('Results exported successfully');
  } catch (error) {
    console.error('Failed to export results:', error);
    alert('导出结果失败');
  }
},

    // 获取文件结构摘要
    async fetchSummaryData() {
      try {
        this.summaryLoading = true;
        this.summaryError = null;
        
        console.log('获取文件结构摘要，session:', this.sessionUuid);
        
        const response = await axios.post('/api/session/Summary/', {
          session: this.sessionUuid
        });
        
        console.log('Summary API响应:', response.data);
        console.log('响应状态:', response.status);
        console.log('响应数据类型:', typeof response.data);
        console.log('响应数据键:', Object.keys(response.data));
        
        if (response.data.success) {
          this.summaryData = response.data;
          console.log('文件结构获取成功，数据已赋值到 summaryData');
          console.log('summaryData 内容:', this.summaryData);
          console.log('directory_structure 存在:', !!this.summaryData.directory_structure);
          if (this.summaryData.directory_structure) {
            console.log('directory_structure 键:', Object.keys(this.summaryData.directory_structure));
            console.log('uploadData:', this.summaryData.directory_structure.uploadData);
            console.log('tidy:', this.summaryData.directory_structure.tidy);
            console.log('buildModel:', this.summaryData.directory_structure.buildModel);
          }
        } else {
          this.summaryError = response.data.message || '获取文件结构失败';
          console.error('获取文件结构失败:', response.data.message);
        }
      } catch (error) {
        console.error('Summary API调用失败:', error);
        this.summaryError = error.message || '网络请求失败';
      } finally {
        this.summaryLoading = false;
      }
    },
    
    // 打开下载弹窗
    downloadAllFiles() {
      if (!this.sessionUuid) {
        alert('会话信息不可用，无法下载文件');
        return;
      }

      this.downloadFileName = `ebio_summary_${this.sessionUuid}`;
      this.downloadDialogVisible = true;
    },

    // 确认下载
    async confirmDownload() {
      if (!this.downloadFileName.trim()) {
        alert('Please enter a file name.');
        return;
      }

      console.log('开始下载所有文件，session:', this.sessionUuid);

      this.downloadLoading = true;
      try {
        const downloadUrl = `/api/session/Summary/download/${this.sessionUuid}/ebio/`;
        const response = await axios.post(downloadUrl, {}, {
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.downloadFileName.trim()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.downloadDialogVisible = false;
      } catch (error) {
        console.error('下载失败:', error);
        alert('下载失败: ' + (error.message || '未知错误'));
      } finally {
        this.downloadLoading = false;
      }
    },

    startNewAnalysis() {
  // 确认用户是否要开始新的分析
  if (confirm('开始新的分析将清除当前所有数据，确定继续吗？')) {
    // 重置所有数据
    this.stepId = 1;
    this.userInputIds = '';
    this.dataFiles = null;
    this.dataFileExisted = false;
    this.fileInfoList = [];
    this.fileUploadStatus = 'idle';
    this.useExampleData = false;
    this.mode = 'EPPD';
    this.lastSuccessfulModel = null;
    this.convertResult = null;
    this.vocaRowData = [];
    this.vocaHeaders = [];
    this.processedNodes = [];
    this.processedEdges = [];
    this.categories = [];
    this.selected = [];
    this.biolinkResult = null;
    this.nodeLoad = false;
    this.biolinkLoading = false;
    
    // 重置摘要数据
    this.summaryData = null;
    this.summaryError = null;
    
    console.log('Started new analysis, all data reset');
  }
}

}
};
</script>

<style scoped>
::-webkit-scrollbar { display: none; }

/* 表格样式 */
::v-deep th, ::v-deep td {
  border: 1px solid #cccccc;
}

.step-content {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

/* 表格容器样式 */
::v-deep(.v-data-table) { overflow: hidden; }
::v-deep(.v-data-table__wrapper) {
  overflow-y: auto;
  max-height: 400px;
  height: 400px;
}
::v-deep(.v-data-table__thead) {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
}

/* Tidy 表格排序图标样式 - 只针对 tidy 表格，不影响复选框 */
.tidy-data-table :deep(.v-data-table-header th) {
  cursor: pointer !important;
}
.tidy-data-table :deep(.v-data-table-header th:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* 排序图标样式优化 */
.tidy-data-table :deep(.v-data-table th.sortable) {
  cursor: pointer;
  user-select: none;
}

.tidy-data-table :deep(.v-data-table th.sortable:hover) {
  background-color: #f5f5f5;
}

/* 确保排序图标可见 */
.tidy-data-table :deep(.v-data-table-header__sort-icon) {
  opacity: 1 !important;
  font-size: 18px;
  color: #1566C0 !important;
}

/* 未排序时的图标也显示（半透明） */
.tidy-data-table :deep(.v-data-table th.sortable .v-data-table-header__sort-icon) {
  opacity: 0.7;
  color: #1566C0 !important;
}

/* 排序激活时的图标完全显示 */
.tidy-data-table :deep(.v-data-table th.sortable.sorted .v-data-table-header__sort-icon) {
  opacity: 1;
  color: #1566C0 !important;
}

/* 自定义排序图标样式（使用 sort-icon 类） */
.tidy-data-table :deep(.sort-icon) {
  color: #1566C0 !important;
}

/* 额外确保所有排序图标都使用指定颜色 */
.tidy-data-table :deep(.v-data-table th.sortable .v-icon) {
  color: #1566C0 !important;
}

/* 分隔符样式 */
.separator-text {
  color: black;
  font-size: 18px;
  font-weight: 500;
  white-space: nowrap;
}

/* 文件信息样式 */
.file-info-section .v-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}
.file-info-section .v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.file-info-section .v-avatar { background-color: #f5f5f5; }
.file-info-section .text-subtitle-2 { color: #1976d2; font-weight: 600; }
.file-info-section .text-caption { color: #666; }

/* 文件项样式 */
.file-item {
  border-radius: 6px;
  transition: background-color 0.2s ease;
}
.file-item:hover { background-color: rgba(0, 0, 0, 0.02); }
.file-item:not(:last-child) {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
  margin-bottom: 12px;
}

/* 按钮样式 */
.example-download-buttons { display: flex; gap: 4px; }
.example-download-buttons .v-btn {
  text-transform: none;
  font-weight: 500;
  font-size: 0.75rem;
  min-width: auto;
  padding: 0 8px;
}

/* 验证提示样式 */
.validation-hint { margin: 12px 0; }
.validation-hint .v-alert {
  border-radius: 8px;
  font-size: 0.875rem;
}

/* 搜索高亮样式 */
.search-highlight {
  background-color: #ffeb3b;
  color: #000;
  padding: 1px 2px;
  border-radius: 2px;
  font-weight: 600;
}

/* 文件结构样式 */
.file-structure .v-list-item {
  border-radius: 4px;
  margin-bottom: 4px;
}
.file-structure .v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

/* 移动端步骤条优化 - 折叠设计 */
@media (max-width: 600px) {
  /* 隐藏所有非当前步骤的文字 */
  :deep(.v-stepper-item:not(.v-stepper-item--selected) .v-stepper-item__title) {
    display: none !important;
  }

  /* 非当前步骤：只显示图标，缩小显示 */
  :deep(.v-stepper-item:not(.v-stepper-item--selected)) {
    opacity: 0.6;
    transform: scale(0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 50px;
  }

  :deep(.v-stepper-item:not(.v-stepper-item--selected) .v-stepper-item__avatar) {
    width: 28px !important;
    height: 28px !important;
    font-size: 12px !important;
  }

  /* 当前步骤：放大显示，显示完整文字 */
  :deep(.v-stepper-item--selected) {
    min-width: 110px;
    opacity: 1;
    transform: scale(1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  :deep(.v-stepper-item--selected .v-stepper-item__avatar) {
    width: 40px !important;
    height: 40px !important;
    font-size: 16px !important;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
  }

  :deep(.v-stepper-item--selected .v-stepper-item__title) {
    display: block !important;
    font-size: 14px !important;
    font-weight: 600;
    color: #1976d2;
    white-space: normal;
    line-height: 1.2;
  }

  /* 步骤条容器优化 */
  :deep(.v-stepper-header) {
    padding: 12px 8px;
    justify-content: space-between;
  }

  /* 连接线样式优化 */
  :deep(.v-stepper-item__divider) {
    margin: 0 4px;
    flex: 0 0 auto;
    min-width: 20px;
  }

  /* Prev/Next 按钮优化 */
  .step-content {
    flex-direction: row;
    justify-content: space-between !important;
    gap: 12px;
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .step-content .v-btn {
    flex: 1;
    min-width: 0;
    height: 48px !important;
    font-size: 16px !important;
    font-weight: 600;
    margin: 0 !important;
  }

  /* 为禁用状态的按钮保持可见性 */
  .step-content .v-btn:disabled {
    opacity: 0.5;
  }

  /* 按钮区域固定在屏幕底部 - 始终可见 */
  .stepper-actionsy {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    z-index: 1000;
    padding: 12px 0;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.15);
  }

  /* 为页面内容添加底部padding，避免被固定按钮遮挡 */
  :deep(.v-stepper) {
    padding-bottom: 80px;
  }
}

/* 方形关闭按钮样式 */
.square-close-btn {
  border-radius: 4px !important;
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
}

.square-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.mode-instruction-content ul,
.mode-instruction-content ol {
  padding-left: 1.2em;
  margin-left: 0;
}

.mode-instruction-content li {
  padding-left: 0.3em;
}
</style>
